import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Chess } from 'chess.js';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import ChessBoardView from '@/components/chess/ChessBoardView';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import LivePuzzle from '@/components/classroom/LivePuzzle';
import LiveStudentBoards from '@/components/classroom/LiveStudentBoards';
import TeacherCaptureLiveView from '@/components/classroom/TeacherCaptureLiveView';
import TeacherPuzzleLiveView from '@/components/classroom/TeacherPuzzleLiveView';
import ClassroomMaterial from '@/components/classroom/ClassroomMaterial';
import ClassroomExplanationView from '@/components/classroom/ClassroomExplanationView';
import ClassroomLavaPresentation from '@/components/classroom/ClassroomLavaPresentation';
import ChallengeIntro from '@/components/classroom/ChallengeIntro';
import ChallengeResponsesPanel from '@/components/classroom/ChallengeResponsesPanel';
import LeaderboardPanel from '@/components/classroom/LeaderboardPanel';
import useChessEngine from '@/hooks/useChessEngine';
import { applyIllegalMove, STARTING_FEN, EMPTY_FEN, playCaptureSound } from '@/lib/chessUtils';
// [Etapa 3.1 — árbol de variantes] Motor local conectado al "modo Variantes"
// del profesor (exploración no destructiva). NO toca backend ni sincronización.
import { createTree, hydrateFromLinearMoves, addMove, navigateTo, getParent, getPath } from '@/lib/moveTree';
import { toast } from 'sonner';
import {
  LogOut, RotateCcw, RotateCw, FlipHorizontal2, SkipBack, SkipForward, ChevronLeft, ChevronRight,
  Copy, Upload, Send, Users, MessageCircle, ListOrdered, Download, Video, VideoOff, ExternalLink,
  Trophy, MoreVertical, UserX, VolumeX, Volume2, Puzzle as PuzzleIcon, Hand,
  Bot, Wand2, AlertTriangle, Pencil, Layers, Eraser, Flag, X, GraduationCap, GitBranch
} from 'lucide-react';

const POLL_MS = 500;



// Fix #5A — referencias estables a nivel de módulo para fallbacks vacíos.
// Evita crear un `[]` / `{}` nuevo en cada render cuando `room.arrows` o
// `room.circles` son null/undefined. Con esto, `<ChessBoardView>` (React.memo
// shallow) puede saltar re-renders cuando estas props no cambian.
// NO se usan como valor mutable — permanecen congelados; cualquier setter
// que necesite un array/objeto nuevo sigue creando su propio literal.
const EMPTY_ARR = [];
const EMPTY_OBJ = {};

// ═══════════════════════════════════════════════════════════════════════════
// [EXP-1.3] PRUEBA TEMPORAL — Desactivar animación 180 ms en Capture Activity
// ═══════════════════════════════════════════════════════════════════════════
// Objetivo: comprobar si la animación del sprite de la pieza (180 ms) está
// provocando el desplazamiento visual lateral / salto / sensación de que
// la pieza se mueve antes de ser tomada durante el uso del tablero de
// explicación con capture_overlay activo.
//
// Ámbito acotado:
//   - Sólo aplica cuando `room.capture_overlay` es truthy (Capture Activity
//     cargada). El tablero de explicación normal (sin overlay) queda 100%
//     intacto: `!!room?.capture_overlay` es false → la expresión completa
//     evalúa a `skipBoardAnim` (comportamiento actual, sin cambio).
//
// Cómo desactivar la prueba:
//   - Cambiar EXP_CAPTURE_DISABLE_ANIM a false → todo vuelve exactamente
//     al comportamiento anterior sin borrar código.
//
// Cómo revertir el cambio completamente:
//   - `git checkout HEAD -- frontend/src/pages/ClassroomLive.jsx`
//   - o buscar `[EXP-1.3]` y eliminar el bloque + volver a
//     `disableAnimation={skipBoardAnim}` en la prop de <ChessBoardView>.
// ═══════════════════════════════════════════════════════════════════════════
const EXP_CAPTURE_DISABLE_ANIM = true;

// ═══════════════════════════════════════════════════════════════════════════
// [EXP-DB-ANIM] PRUEBA TEMPORAL — Desactivar animación 180 ms ÚNICAMENTE en
// Ejercicios de Base de Datos (loadGameToBoard) para diagnosticar la
// desaparición visual de piezas (dama, alfil, torre, capturas) durante los
// 180 ms de animación de react-chessboard.
// ═══════════════════════════════════════════════════════════════════════════
// Ámbito acotado — se activa SÓLO cuando se cumplen las 3 condiciones:
//   1. `room.starting_fen` existe y NO es la posición inicial estándar
//      (rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1).
//      → Verdadero cuando el profesor cargó un ejercicio de la Base de
//        Datos (loadGameToBoard fija starting_fen al FEN inicial del
//        ejercicio) o un FEN custom vía edit.
//   2. NO hay `capture_overlay` activo (Capture Activity ya deshabilita
//      animación con EXP_CAPTURE_DISABLE_ANIM — evitamos doble aplicación).
//   3. NO hay `active_puzzle` activo (puzzles tácticos push a alumnos usan
//      su propio pipeline LivePuzzle, no el tablero de explicación).
//
// Efecto: `disableAnimation` pasa a true en el tablero → prop
// `animationDurationInMs: 0` en react-chessboard → línea 4898-4900 de
// la librería salta el pipeline de intermediatePosition / positionUpdates
// / waitingForAnimationPosition y aplica `setCurrentPosition(newPosition)`
// síncronamente. Si la pieza deja de desaparecer visualmente confirmamos
// la hipótesis primaria de la auditoría (desambiguación heurística mala).
//
// Cero cambios a:
//   - onPieceDrop, applyLocalMove, pushFen, /move, /load, /view_ply
//   - POLL_MS (sigue 1000ms), polling, sincronización profesor/alumno
//   - ChessBoardView (no se toca)
//   - Capture Activity (su propia rama EXP_CAPTURE_DISABLE_ANIM intacta)
//   - Classroom normal (starting_fen === default → condición 1 falsa)
//   - Sonidos, moveTree.js, navegación histórica, FEN, backend, BD
//
// Cómo desactivar la prueba en 1 línea:
//   - Cambiar EXP_DB_DISABLE_ANIM a false → comportamiento anterior 1:1.
//
// Cómo revertir el cambio completamente:
//   - Buscar `[EXP-DB-ANIM]` y eliminar el bloque + volver a
//     `disableAnimation={skipBoardAnim || (EXP_CAPTURE_DISABLE_ANIM && !!room?.capture_overlay)}`
//     en la prop de <ChessBoardView> (línea ~1520).
//   - o `git diff HEAD -- frontend/src/pages/ClassroomLive.jsx` para ver el
//     delta exacto y `git checkout HEAD -- ...` para descartarlo.
// ═══════════════════════════════════════════════════════════════════════════
const EXP_DB_DISABLE_ANIM = true;
const DEFAULT_STARTING_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';


export default function ClassroomLive() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [error, setError] = useState(null);
  const [chatText, setChatText] = useState('');
  const [orientation, setOrientation] = useState('white');

  // Sincronización Classroom: la orientación viene del estado compartido de la sala.
  useEffect(() => {
    if (room?.board_orientation === 'white' || room?.board_orientation === 'black') {
      setOrientation(room.board_orientation);
    }
  }, [room?.board_orientation]);
  const [ply, setPly] = useState(null); // null = live/current; number = viewing history
  // [Stage 3.1] Modo "Variantes": exploración LOCAL no destructiva sobre moveTree.
  // OFF por defecto → el tablero en vivo se comporta exactamente igual que hoy.
  // ON → el tablero se pinta desde el nodo actual del árbol y las jugadas se
  // guardan como ramas locales (sin POST a backend, sin tocar la sincronización).
  const [analysisMode, setAnalysisMode] = useState(false);
  // [Stage 3.1 · auto] Bandera vinculada AL EVENTO real de carga de un ejercicio
  // en el tablero del profesor (loadGameToBoard, que usa is_new_exercise=true).
  // NO se infiere del FEN. Solo se pone en true dentro del flujo de carga y en
  // false al salir/resetear o al entrar a Capture/Puzzle/Explanation.
  const [exerciseLoaded, setExerciseLoaded] = useState(false);
  // Ref para que el manejador de teclado (definido antes que los handlers de
  // navegación del árbol) pueda enrutar ←/→ al árbol en modo Variantes sin TDZ.
  const analysisNavRef = useRef(null);
  const analysisGotoPlyRef = useRef(null);
  // [Stage 3.1 · remount controlado] Época de NAVEGACIÓN del árbol. Se
  // incrementa SOLO al navegar entre nodos (back/next/first/last/goto), NUNCA
  // al crear una jugada (addMove). Se usa como `key` del <ChessBoardView> en
  // modo Variantes para forzar una instancia limpia de react-chessboard/@dnd-kit
  // tras navegar, resolviendo el bloqueo del sensor al revertir la posición.
  const [navEpoch, setNavEpoch] = useState(0);

  // ───────────────────────────────────────────────────────────────────────
  // [Etapa 2 — árbol de variantes] Estado local paralelo al historial actual.
  // ───────────────────────────────────────────────────────────────────────
  // Vive en paralelo a `room.moves` + `ply` (que siguen siendo la fuente de
  // verdad). En esta etapa NO se conecta con onPieceDrop, applyLocalMove,
  // goNext/goPrev/goFirst/goLive, reset, ni con la lista visual de jugadas.
  // Sólo se hidrata en el useEffect de abajo cuando cambia la sesión o la
  // posición inicial. Reversión trivial: eliminar este estado y el effect.
  const [moveTree, setMoveTree] = useState(null);
  const moveTreeRef = useRef(null);
  const [treeViewActive, setTreeViewActive] = useState(false);
  const [openLoad, setOpenLoad] = useState(false);
  const [pgnInput, setPgnInput] = useState('');
  const [fenInput, setFenInput] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);
  const [showMaterial, setShowMaterial] = useState(false);
  const [activeBottomTab, setActiveBottomTab] = useState('moves');
  const [takingAttendance, setTakingAttendance] = useState(false);
  const attendancePanelRef = useRef(null);
  // E2 · Ciclo de vida: confirmación de "Terminar clase".
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [endingClass, setEndingClass] = useState(false);
  const [launchCfg, setLaunchCfg] = useState(null);
  const [boardSize, setBoardSize] = useState(560);
  // UX-2 · aside colapsable: al cerrarlo se DESMONTA (no CSS) para que el
  // contenedor del tablero reciba el nuevo ancho y el ResizeObserver recalcule.
  const [asideOpen, setAsideOpen] = useState(true);
  // UX-4 · Panel secundario del ALUMNO como overlay (Sheet lateral ≥md /
  // Drawer inferior en móvil). Inicia CERRADO para que el tablero sea el
  // elemento dominante desde el primer instante. No afecta al profesor.
  const [panelOpen, setPanelOpen] = useState(false);
  // Chat flotante del PROFESOR (Opción A). Solo controla la visibilidad de una
  // capa de presentación sobre Classroom; reutiliza room.chat/sendChat/unread.
  // No toca el panel lateral ni el estado de la clase. Exclusivo profesor.
  const [chatPopoverOpen, setChatPopoverOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // FASE A · Challenge Intro (SOLO alumno). El guard "una sola vez" (stepKey) y
  // el timer viven aquí (padre estable, no se re-monta con el polling).
  const [introVisible, setIntroVisible] = useState(false);
  const [introType, setIntroType] = useState('square');
  const lastIntroKeyRef = useRef(null);
  const introTimerRef = useRef(null);
  // Fix #4 — señal one-shot para saltar la animación de react-chessboard en
  // el próximo commit del FEN, activada sólo por Load Game (cambio brusco).
  // Se resetea automáticamente vía useEffect en cuanto ChessBoardView
  // recibe el nuevo FEN sin animación, así los movimientos normales
  // posteriores vuelven a animarse con normalidad.
  const [skipBoardAnim, setSkipBoardAnim] = useState(false);
  // Click-to-move on the teacher's main board (Method 1). Drag & drop still works.
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalDests, setLegalDests] = useState({}); // { dst: { isCapture: bool } }
  const boardWrapRef = useRef(null);
  const chatEndRef = useRef(null);
  const pollRef = useRef(null);
  // [Stage 3.1 · auto diferido] FEN del ejercicio pendiente de auto-Variantes.
  // Se registra en loadGameToBoard y se consume UNA sola vez cuando el poll del
  // servidor confirma la reconciliación (fetchRoom). Un ref (no estado) evita
  // renders extra y sirve de guard anti-duplicados.
  const pendingAutoAnalysisFenRef = useRef(null);
  const maybeConsumeAutoAnalysisRef = useRef(null);
  // [Stage 3.2] PGN oficial pendiente de sembrar como ramas oficiales, y el
  // conjunto de ids de nodos oficiales (separación oficial/local FUERA del árbol
  // — moveTree.js no se modifica). Los nodos creados después = locales.
  const pendingAutoAnalysisPgnRef = useRef(null);
  const officialNodeIdsRef = useRef(new Set());
  // --- Single-source-of-truth guards (see RCA notes at top of file) ---
  const moveLockRef = useRef(false);          // prevents double-fire of onPieceDrop while POST in flight
  const optimisticLenRef = useRef(0);         // minimum moves.length polling must return to be accepted
  const optimisticFenRef = useRef(null);      // last locally-applied FEN (for load/reset ops with no moves)
  const optimisticVersionRef = useRef(0);     // monotonic counter for load/edit ops
  const suppressPollUntilRef = useRef(0);     // ignore poll responses until this timestamp
  // Paso 1.1 (Fix perf drag pickup) — cuando el profesor está arrastrando
  // una pieza, pausamos fetchRoom para evitar que un re-render provocado
  // por polling resetee el sensor de @dnd-kit y obligue al profesor a
  // volver a tomar la pieza. Se libera automáticamente en: drop válido,
  // drop inválido, drop fuera, cancel/escape, safety-release (5s) y
  // unmount. Sólo se activa para el profesor: para el alumno,
  // `arePiecesDraggable` es false → la librería nunca dispara callbacks
  // de drag → este ref queda siempre en false y su polling nunca se pausa.
  const dragActiveRef = useRef(false);

  // Board navigation callbacks — shared by the toolbar buttons AND the ← / →
  // keyboard shortcuts below. Declared here (before any early returns) so
  // the hook order stays consistent across renders. Fresh moves length is
  // read via a ref so the callbacks stay stable (identity never changes).
  const movesLenRef = useRef(0);

  // ─────────────────────────────────────────────────────────────────────
  // Etapa 1 — Sincronizar navegación histórica del profesor con el alumno
  // ─────────────────────────────────────────────────────────────────────
  // Refs para leer estado fresco desde callbacks estables ([] deps).
  const roomRef       = useRef(null);       // último `room` observado
  const plyRef        = useRef(null);       // último `ply` observado
  const isTeacherRef  = useRef(false);      // rol del usuario
  const liveFenRef    = useRef(null);       // FEN "vivo real" (última jugada), independiente de room.fen
                                            // — porque view_ply puede sobreescribir temporalmente room.fen con
                                            // posiciones históricas y necesitamos conocer la FEN viva verdadera
                                            // cuando el profesor pulsa goLive.

  // Deriva la FEN correspondiente a un ply, usando la misma semántica que el
  // useMemo `boardFen` de más abajo pero de forma síncrona (sin esperar al
  // próximo render). Regla:
  //   ply === null  → live real = última jugada del array (no room.fen porque
  //                   view_ply puede haberla sustituido); fallback: starting_fen.
  //   ply === 0     → starting_fen (posición inicial del ejercicio/sala).
  //   ply === N > 0 → snapshot fen almacenado en moves[N-1].
  const computeFenForPly = useCallback((targetPly) => {
    const r = roomRef.current;
    if (!r) return null;
    const moves = r.moves || [];
    const total = moves.length;
    const startingFen = r.starting_fen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
    if (targetPly === null || targetPly >= total) {
      return liveFenRef.current || (total > 0 ? moves[total - 1]?.fen : startingFen);
    }
    if (targetPly === 0) return startingFen;
    return moves[targetPly - 1]?.fen || null;
  }, []);

  // Optimistically actualiza room.fen local + POST /view_ply al backend.
  // Sólo el profesor emite; los alumnos que naveguen historial localmente
  // NO transmiten nada (sus refs plyRef/roomRef existen pero el guard
  // `isTeacherRef.current` cortocircuita el POST).
  const syncViewPly = useCallback((targetPly) => {
    if (!isTeacherRef.current) return;
    const targetFen = computeFenForPly(targetPly);
    if (!targetFen) return;
    // Optimista local: al retroceder, `boardFen` ya usa moves[ply-1].fen (no
    // room.fen), pero al hacer goLive `boardFen` sí lee room.fen, así que
    // mantenerlo alineado con la FEN transmitida evita un flash cuando el
    // poll del backend llega con esa misma FEN.
    setRoom(prev => (prev && prev.fen !== targetFen) ? { ...prev, fen: targetFen } : prev);
    // Suprimir el próximo poll para evitar que reemplace nuestra copia
    // optimista con la respuesta del backend (que dice lo mismo). Ventana
    // corta — no interfiere con jugadas nuevas.
    suppressPollUntilRef.current = Date.now() + 500;
    // Fire-and-forget; error silencioso — no debe romper la vista local.
    api.post(`/classrooms/${id}/view_ply`, { fen: targetFen })
       .catch(() => { /* silent */ });
  }, [id, computeFenForPly]);

  const goFirst = useCallback(() => {
    setPly(0);
    syncViewPly(0);
  }, [syncViewPly]);
  const goPrev  = useCallback(() => {
    const nav = analysisNavRef.current;

    if (nav?.treeViewActive) {
      nav.back();
      return;
    }

    const cur = plyRef.current ?? movesLenRef.current;
    const target = Math.max(0, cur - 1);

    if (analysisGotoPlyRef.current) {
      analysisGotoPlyRef.current(target);
      return;
    }

    setPly(target);
    syncViewPly(target);
  }, [syncViewPly]);
  const goNext  = useCallback(() => {
    const len = movesLenRef.current;
    const cur = plyRef.current ?? len;
    const nxt = Math.min(len, cur + 1);
    const target = nxt >= len ? null : nxt;
    setPly(target);
    syncViewPly(target);
  }, [syncViewPly]);
  const goLive  = useCallback(() => {
    setTreeViewActive(false);
    setPly(null);
    syncViewPly(null);
  }, [syncViewPly]);
  useEffect(() => {
    const isEditable = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (el.isContentEditable) return true;
      const role = el.getAttribute && el.getAttribute('role');
      if (role === 'textbox' || role === 'combobox') return true;
      return false;
    };
    const onKey = (e) => {
      // Never steal keys while the user is typing in a form/field.
      if (isEditable(e.target)) return;
      // Only ← / → for now. No dependency on studentExerciseMode or any
      // other later-declared const → no TDZ risk. The handlers themselves
      // are safe no-ops when there are no moves (goPrev clamps to 0,
      // goNext returns null which means "live").
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      // [Stage 3.1] En modo Variantes, ←/→ navegan el árbol (no ply).
      const nav = analysisNavRef.current;
      if (nav && nav.treeViewActive) {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); nav.back(); return; }
        if (e.key === 'ArrowRight') { e.preventDefault(); nav.next(); return; }
        return;
      }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); goPrev();  return; }
      if (e.key === 'ArrowRight') { e.preventDefault(); goNext();  return; }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext]);

  const isTeacher = room && room.teacher_user_id === user?.id;
  const isReadOnly = !isTeacher;
  // E2 · Solo para "Terminar clase" (NO sustituye isTeacher). owner + admins.
  const canManage = isTeacher || user?.role === 'school_admin' || user?.role === 'super_admin';
  // E2 · Clase terminada: active=false o ended_at presente (incluye legacy).
  const classEnded = !!room && (room.active === false || !!room.ended_at);

  // ─────────────────────────────────────────────────────────────────────
  // Etapa 1 — Sync refs para las callbacks de navegación (identidad estable).
  // ─────────────────────────────────────────────────────────────────────
  // Los refs viven en la sección de estado (líneas ~149-155). Aquí los
  // sincronizamos con los valores actuales. Cero setState, cero re-render:
  // sólo mutación de refs. Los callbacks estables (goPrev/goNext/etc.) los
  // leen a través de `plyRef.current` / `roomRef.current` etc.
  useEffect(() => { roomRef.current = room; }, [room]);
  useEffect(() => { plyRef.current = ply; }, [ply]);
  useEffect(() => { isTeacherRef.current = !!isTeacher; }, [isTeacher]);

  // Fase 2 · Asistencia: al abrir "Pasar lista", colocar el panel
  // en la parte superior antes del primer pintado del nuevo layout.
  useLayoutEffect(() => {
    if (takingAttendance && attendancePanelRef.current) {
      attendancePanelRef.current.scrollTop = 0;
    }
  }, [takingAttendance]);

  // liveFenRef mantiene la FEN "viva verdadera" independiente de room.fen
  // (que view_ply puede sobreescribir con posiciones históricas). Se
  // deriva de moves[last].fen o, si no hay jugadas, de starting_fen.
  // Observa moves + starting_fen — ambos son campos que view_ply NO toca,
  // por lo que este ref queda siempre correcto.
  useEffect(() => {
    const moves = room?.moves || [];
    if (moves.length > 0) {
      liveFenRef.current = moves[moves.length - 1]?.fen || liveFenRef.current;
    } else if (room?.starting_fen) {
      liveFenRef.current = room.starting_fen;
    }
  }, [room?.moves, room?.starting_fen]);

  // Teacher tools
  const [illegalMode, setIllegalMode] = useState(false);
  const [vsEngine, setVsEngine] = useState(false);
  const [engineLevel, setEngineLevel] = useState(5);
  const [engineSide, setEngineSide] = useState('b'); // engine plays black by default
  const [openEdit, setOpenEdit] = useState(false);
  const [editFen, setEditFen] = useState('');
  const [editTool, setEditTool] = useState(null);
  const [editCastling, setEditCastling] = useState('-');
  const [editEnPassant, setEditEnPassant] = useState('-');
  const [pendingPromotion, setPendingPromotion] = useState(null); // { from, to, color }
  const engine = useChessEngine();

  // Editor visual de posición: modifica únicamente la copia local editFen.
  const updateEditSquare = useCallback((square, piece) => {
    try {
      const board = new Chess(editFen || STARTING_FEN, { skipValidation: true });
      board.remove(square);
      if (piece) {
        board.put({ type: piece[1].toLowerCase(), color: piece[0] }, square);
      }
      const parts = board.fen().split(' ');
      parts[2] = editCastling || '-';
      parts[3] = editEnPassant || '-';
      setEditFen(parts.join(' '));
      return true;
    } catch {
      return false;
    }
  }, [editFen, editCastling, editEnPassant]);

  const updateEditSpecialFields = useCallback((castling, enPassant) => {
    const parts = (editFen || STARTING_FEN).split(' ');
    while (parts.length < 6) parts.push('-');
    parts[2] = castling || '-';
    parts[3] = enPassant || '-';
    setEditFen(parts.join(' '));
    setEditCastling(castling || '-');
    setEditEnPassant(enPassant || '-');
  }, [editFen]);

  // -----------------------------------------------------------------------
  // Single source of truth = local `room` state.
  // Polling reconciles ONLY if server reflects at least our optimistic moves.
  // If server is behind (POST not yet processed), the poll is dropped so the
  // board never "reverts" to a stale position.
  // -----------------------------------------------------------------------
  const fetchRoom = useCallback(async () => {
    // Paso 1.1 — Guard PRIMERO: si el profesor está arrastrando una pieza,
    // salta este tick por completo (no dispara la petición). Cero cambio
    // a peticiones ya en curso (no cancelamos requests en vuelo). El
    // siguiente tick del setInterval reanudará el polling apenas el drag
    // termine. Cero impacto para el alumno (su dragActiveRef siempre
    // vale false porque arePiecesDraggable={false} para él).
    if (dragActiveRef.current) {
      return;
    }
    try {
      const { data } = await api.get(`/classrooms/${id}`);
      // Do not touch state while a local move is being posted
      if (moveLockRef.current) { return; }
      // Ignore stale polls that happen right after load/edit/undo
      if (Date.now() < suppressPollUntilRef.current) { return; }
      // [Stage 3.1 · auto diferido] Señal REAL de reconciliación: este es el
      // primer poll del servidor ACEPTADO tras la carga (ya expiró la ventana
      // de supresión). Consumimos aquí el pending → el tablero de análisis se
      // monta en una ventana quiescente, igual que la ruta MANUAL.
      maybeConsumeAutoAnalysisRef.current?.(data);
      setRoom(prev => {
        if (!prev) { return data; }
        const serverLen = (data.moves || []).length;
        // If we have applied moves locally that server hasn't seen yet -> drop this poll
        if (serverLen < optimisticLenRef.current) { return prev; }
        // Keep our transient annotations if a save is pending
        if (prev._pendingAnnots) {
          return { ...data, arrows: prev.arrows, circles: prev.circles, _pendingAnnots: true };
        }
        // FASE 5F Fix perf — Salta el re-render cuando el servidor no ha
        // cambiado nada desde el último poll.
        //
        // Contexto: el polling cada 2 s reemplaza el objeto `room` en cada
        // tick incluso cuando el servidor está inmóvil, provocando una
        // cascada de re-renders (ChessBoardView, TeacherCaptureLiveView,
        // TeacherPuzzleLiveView, moves list, chat). Ese ruido es el que
        // percibe el usuario como "traba" al pulsar Cargar.
        //
        // Comparación conservadora: `updated_at` (que 15/29 mutaciones del
        // backend ya actualizan) más contadores de arrays que se modifican
        // en endpoints que NO tocan updated_at (`puzzle_solves` L1577,
        // `raised_hands` en $pull, etc). Si CUALQUIER campo difiere,
        // no saltamos y hacemos el render habitual.
        const prevPresence = (prev.participants || [])
          .map(p => `${p.user_id}:${p.is_present ? 1 : 0}`)
          .join('|');
        const dataPresence = (data.participants || [])
          .map(p => `${p.user_id}:${p.is_present ? 1 : 0}`)
          .join('|');

        if (
          prev.updated_at === data.updated_at &&
          (prev.moves?.length || 0)          === (data.moves?.length || 0) &&
          (prev.chat?.length || 0)           === (data.chat?.length || 0) &&
          (prev.puzzle_solves?.length || 0)  === (data.puzzle_solves?.length || 0) &&
          (prev.raised_hands?.length || 0)   === (data.raised_hands?.length || 0) &&
          (prev.participants?.length || 0)   === (data.participants?.length || 0) &&
          JSON.stringify(prev.puzzle_states || {}) === JSON.stringify(data.puzzle_states || {}) &&
          JSON.stringify(prev.active_explanation?.lava_state || null) ===
            JSON.stringify(data.active_explanation?.lava_state || null) &&
          JSON.stringify(prev.active_explanation?.explore_progress || {}) ===
            JSON.stringify(data.active_explanation?.explore_progress || {}) &&
          prevPresence === dataPresence
        ) {
          return prev;
        }
        return data;
      });
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo cargar la clase');
      if (pollRef.current) clearInterval(pollRef.current);
    }
  }, [id]);

  useEffect(() => {
    fetchRoom();
    pollRef.current = setInterval(fetchRoom, POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [id, fetchRoom]);

  // Fase 1 · Presencia automática: registra que el usuario sigue dentro
  // del Classroom sin alterar el polling existente ni updated_at.
  useEffect(() => {
    let presenceRef = null;

    const sendPresence = () => {
      api.post(`/classrooms/${id}/presence`).catch(() => {});
    };

    sendPresence();
    presenceRef = setInterval(sendPresence, 15000);

    return () => {
      if (presenceRef) clearInterval(presenceRef);
    };
  }, [id]);

  useEffect(() => {
    if (isTeacher) api.get('/exercises').then(r => setAvailablePuzzles(r.data || [])).catch(()=>{});
  }, [isTeacher]);

  // Dynamic board sizing — keeps the board 1:1 while fitting the available column
  // and viewport height. Reserves room for the header + toolbar + switch row.
  useEffect(() => {
    const el = boardWrapRef.current;
    if (!el) return;
    const measure = () => {
      const width = el.clientWidth;
      // Vertical budget: total viewport minus header (~64) + paddings (~32) +
      // toolbar (~60) + moves-visible row (~40) + safety. Material now embedded
      // in the sidebar top section, so it no longer affects the board height.
      // UX-4 · el alumno tiene una fila de acciones (Herramientas / Levantar
      // mano) sobre el tablero → reservamos un poco más de alto vertical para
      // que el tablero no quede recortado. El profesor mantiene UX-2 (220).
      const reserve = 210;
      const availH = window.innerHeight - reserve;
      const size = Math.max(280, Math.min(900, Math.min(width, availH)));
      setBoardSize(Math.floor(size));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [showMaterial, room?.active_puzzle, room?.active_explanation, isTeacher, asideOpen]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({behavior:'smooth'}); }, [room?.chat?.length]);

  // UX-4 · Detecta breakpoint móvil (<768px) para decidir la presentación del
  // panel secundario del alumno: Drawer inferior en móvil, Sheet lateral en
  // tablet/desktop. Solo controla presentación; no toca lógica de la clase.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const on = () => setIsMobile(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  // FASE A · Detección del inicio de Challenge (SOLO alumno). Fuente de verdad:
  // active_explanation.activity.steps[current_step]. stepKey = id + current_step.
  // Las deps son PRIMITIVAS (id, current_step, isTeacher) → el efecto solo corre
  // al cambiar de paso oficial, NUNCA por polling ni por estado interno del
  // Viewer. lastIntroKeyRef garantiza "una sola vez" incluso si el efecto
  // vuelve a evaluarse. No hace requests ni toca ningún estado de la clase.
  useEffect(() => {
    if (isTeacher) return;
    const ae = room?.active_explanation;
    const steps = Array.isArray(ae?.activity?.steps) ? ae.activity.steps : null;
    if (!steps || steps.length === 0) return;
    const cur = Math.max(0, Math.min(Number(ae?.current_step) || 0, steps.length - 1));
    const st = steps[cur] || {};
    if (st.mode !== 'challenge') { setIntroVisible(false); return; }
    const key = `${ae?.id}:${cur}`;
    if (lastIntroKeyRef.current === key) return;
    lastIntroKeyRef.current = key;
    setIntroType(st?.challenge?.type === 'move' ? 'move' : 'square');
    setIntroVisible(true);
    if (introTimerRef.current) clearTimeout(introTimerRef.current);
    introTimerRef.current = setTimeout(() => setIntroVisible(false), 1700);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeacher, room?.active_explanation?.id, room?.active_explanation?.current_step]);
  useEffect(() => () => { if (introTimerRef.current) clearTimeout(introTimerRef.current); }, []);

  // ── FASE B · Chat Notification (badge + toast) · estado LOCAL ─────────────
  // Fuente de verdad: room.chat del polling existente. `unread` se calcula vs un
  // marcador `lastSeen` (ts del último mensaje visto) persistido en localStorage
  // POR SALA. Idempotente: re-polling del mismo array NO infla el contador ni
  // re-toasta. No toca backend ni el sistema de chat, solo añade señal visual.
  const chatSeenKey = `c4k_chat_seen_${id}`;
  const [chatLastSeenTs, setChatLastSeenTs] = useState('');
  const chatInitRef = useRef(false);
  const lastToastTsRef = useRef('');

  // Init una vez: si hay marcador guardado se respeta (unread sobrevive recarga);
  // si no, se toma el ts del último mensaje actual → el historial NO cuenta como
  // no leído ni genera toasts al entrar/recargar.
  useEffect(() => {
    if (chatInitRef.current || !room) return;
    const chat = room.chat || [];
    const lastTs = chat.length ? (chat[chat.length - 1].ts || '') : '';
    let stored = null;
    try { stored = localStorage.getItem(chatSeenKey); } catch { stored = null; }
    setChatLastSeenTs(stored || lastTs);
    lastToastTsRef.current = lastTs;
    chatInitRef.current = true;
  }, [room, chatSeenKey]);

  // ¿El chat está REALMENTE visible? (condición real, no solo panel abierto)
  const chatVisible = isTeacher
    ? (chatPopoverOpen || (asideOpen && !showMaterial && activeBottomTab === 'chat'))
    : (panelOpen && activeBottomTab === 'chat');

  // No leídos: mensajes de OTROS con ts posterior al marcador. Memo idempotente.
  const unreadChat = useMemo(() => {
    if (!chatInitRef.current) return 0;
    const chat = room?.chat || [];
    return chat.filter(m => m.user_id !== user?.id && (m.ts || '') > chatLastSeenTs).length;
  }, [room?.chat, chatLastSeenTs, user?.id]);

  // Marcar como VISTO solo cuando el chat está visible de verdad.
  useEffect(() => {
    if (!chatInitRef.current || !chatVisible) return;
    const chat = room?.chat || [];
    const lastTs = chat.length ? (chat[chat.length - 1].ts || '') : '';
    if (lastTs && lastTs !== chatLastSeenTs) {
      setChatLastSeenTs(lastTs);
      try { localStorage.setItem(chatSeenKey, lastTs); } catch { /* noop */ }
    }
    if (lastTs) lastToastTsRef.current = lastTs;
  }, [chatVisible, room?.chat, chatLastSeenTs, chatSeenKey]);

  // Toast COALESCIDO cuando llegan mensajes nuevos de otros y el chat NO está
  // visible. Avanza el marcador de toast → sin duplicados por polling.
  useEffect(() => {
    if (!chatInitRef.current || chatVisible) return;
    const chat = room?.chat || [];
    const fresh = chat.filter(m => m.user_id !== user?.id && (m.ts || '') > lastToastTsRef.current);
    if (fresh.length === 0) return;
    lastToastTsRef.current = chat.length ? (chat[chat.length - 1].ts || '') : lastToastTsRef.current;
    const last = fresh[fresh.length - 1];
    if (fresh.length === 1) {
      toast(`💬 ${last.name}`, { description: last.text, duration: 4000 });
    } else {
      toast(`💬 ${fresh.length} mensajes nuevos`, { description: `${last.name}: ${last.text}`, duration: 4000 });
    }
  }, [room?.chat, chatVisible, user?.id]);

  // Fix #4 — Una vez el nuevo FEN ha commiteado en el DOM con animación
  // desactivada, reactivamos la animación normal para las jugadas que vengan
  // después (drag & drop, engine, etc.). El reset se hace en el siguiente
  // tick para garantizar que react-chessboard ya haya procesado el diff
  // FEN → FEN sin animar. Es una señal one-shot: sólo se activa desde
  // loadGameToBoard y se desactiva sola.
  useEffect(() => {
    if (!skipBoardAnim) return;
    const t = setTimeout(() => setSkipBoardAnim(false), 0);
    return () => clearTimeout(t);
  }, [skipBoardAnim, room?.fen]);

  // ───────────────────────────────────────────────────────────────────────
  // [Etapa 2 — árbol de variantes] Hidratación segura del árbol local.
  // ───────────────────────────────────────────────────────────────────────
  // Reinicializa el árbol cuando cambia la sesión (id) o la posición inicial
  // (`room.starting_fen`), que son los únicos eventos que invalidan la raíz:
  //   - Primer fetch de la sala (starting_fen pasa de undefined → string).
  //   - Load de un ejercicio / DB / PGN (server.py:1970 actualiza starting_fen).
  //   - Reset del tablero (ClassroomLive:884 sobreescribe starting_fen).
  //   - Cambio de classroom (`id` cambia).
  //
  // NO depende de `room.moves.length` — por diseño de Etapa 2 el árbol NO
  // reacciona a jugadas nuevas del profesor (esa integración es Etapa 3).
  // Como consecuencia, mientras el profesor juega sobre la mainline, el
  // árbol queda "congelado" en el estado del último re-hidrate. Perfectamente
  // aceptable en esta etapa — sólo estamos verificando que se inicializa.
  //
  // Reconstruye el árbol cuando cambia la sesión/posición inicial.
  // Para jugadas nuevas de room.moves usamos sincronización incremental:
  // agrega únicamente la parte oficial que falta y conserva las ramas locales.
  useEffect(() => {
    if (!room) return;

    const startingFen = room.starting_fen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQkBNR w KQkq - 0 1';
    const linearMoves = Array.isArray(room.moves) ? room.moves : [];

    const tree = linearMoves.length > 0
      ? hydrateFromLinearMoves(startingFen, linearMoves)
      : createTree(startingFen);

    setMoveTree(tree);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, room?.starting_fen]);

  // Ref del árbol actual para que la sincronización oficial pueda
  // trabajar sin depender de cambios locales de moveTree.
  useEffect(() => {
    moveTreeRef.current = moveTree;
  }, [moveTree]);

  // [Etapa 3 · sync incremental] Integra nuevas jugadas oficiales de
  // room.moves sin reconstruir el árbol y sin destruir variantes locales.
  //
  // Regla:
  //   - childrenIds[0] = línea principal/oficial.
  //   - hijos posteriores = alternativas creadas localmente.
  //   - currentId se conserva aunque addMove() avance temporalmente al nodo
  //     agregado.
  useEffect(() => {
    if (!room || !moveTree || !Array.isArray(room.moves)) return;
    if (room.moves.length === 0) return;

    const officialMoves = room.moves;
    const currentTree = moveTreeRef.current;
    if (!currentTree) return;

    let tree = currentTree;
    let parentId = tree.rootId;
    let changed = false;

    // Si la línea oficial existente ya no coincide con room.moves (por ejemplo,
    // un Load/Reset que mantuvo el mismo starting_fen), no intentamos mezclar
    // estados incompatibles: el efecto de starting_fen normalmente reconstruye
    // el árbol, y aquí solo abandonamos la sincronización.
    let mismatch = false;

    for (let i = 0; i < officialMoves.length; i++) {
      const m = officialMoves[i];
      if (!m || typeof m.san !== 'string' || typeof m.fen !== 'string') continue;

      const parent = tree.nodes[parentId];
      if (!parent) {
        mismatch = true;
        break;
      }

      const children = parent.childrenIds || [];
      const mainlineId = children[0];
      const mainlineNode = mainlineId ? tree.nodes[mainlineId] : null;

      const sameMove = mainlineNode?.move &&
        mainlineNode.move.san === m.san &&
        mainlineNode.move.from === m.from &&
        mainlineNode.move.to === m.to &&
        (mainlineNode.move.promotion || null) === (m.promotion || null);

      if (sameMove) {
        parentId = mainlineNode.id;
        continue;
      }

      // Si no existe la jugada oficial en la primera posición de hijos,
      // significa que el árbol todavía no contiene esta parte de la mainline.
      // La añadimos como hijo oficial.
      // La jugada oficial debe ocupar childrenIds[0] para conservar
      // la semántica de mainline del motor moveTree.
      const newId = `official-${Date.now().toString(36)}-${i}`;
      const newNode = {
        id: newId,
        parentId: parent.id,
        childrenIds: [],
        move: {
          san: m.san,
          fen: m.fen,
          from: m.from,
          to: m.to,
          ...(m.promotion ? { promotion: m.promotion } : {}),
        },
      };

      tree = {
        ...tree,
        nodes: {
          ...tree.nodes,
          [parent.id]: {
            ...parent,
            childrenIds: [newId, ...parent.childrenIds],
          },
          [newId]: newNode,
        },
      };

      parentId = newId;
      changed = true;
    }

    if (mismatch || !changed) return;

    // addMove() mueve currentId al último nodo agregado. La sincronización de
    // room.moves NO debe cambiar lo que el profesor está viendo.
    if (tree.currentId !== currentTree.currentId) {
      tree = navigateTo(tree, currentTree.currentId);
    }

    setMoveTree(tree);
  }, [room?.moves]);

  // [Stage 3.1 · auto] Guard de exclusión defensivo: si la sala entra en
  // Capture Activity / Puzzle push / Explanation, la auto-Variantes se apaga
  // SIEMPRE (independiente de la vía de carga). Solo profesor.
  useEffect(() => {
    if (!isTeacher) return;
    if (room?.capture_overlay || room?.active_puzzle || room?.active_explanation) {
      pendingAutoAnalysisFenRef.current = null;
      pendingAutoAnalysisPgnRef.current = null;
      officialNodeIdsRef.current = new Set();
      setExerciseLoaded(false);
      setAnalysisMode(false);
    }
  }, [isTeacher, room?.capture_overlay, room?.active_puzzle, room?.active_explanation]);

  // ── [Stage 3.1] Modo Variantes — exploración local no destructiva ──────────
  // Entrar: siembra el árbol desde la línea en vivo actual (starting_fen +
  // moves) para que el profesor pueda ramificar desde la posición actual.
  const enterAnalysis = useCallback(() => {
    // [Stage 3.1 · integración] Variantes utiliza el árbol persistente de
    // Classroom Live. NO crea un árbol paralelo ni reinicia la mainline.
    const startingFen = room?.starting_fen || STARTING_FEN;

    let tree = moveTree;
    if (!tree) {
      tree = createTree(startingFen);
    }

    // Determina la posición desde la que debe comenzar el análisis:
    //   - si el profesor está viendo historia, respeta ese ply;
    //   - si está en vivo, entra en la última posición de la mainline oficial.
    const totalMoves = Array.isArray(room?.moves) ? room.moves.length : 0;
    const targetPly = ply === null
      ? totalMoves
      : Math.max(0, Math.min(ply, totalMoves));

    let targetId = tree.rootId;

    // La mainline oficial siempre vive en childrenIds[0].
    // Recorremos únicamente esa rama para llegar al punto solicitado.
    for (let i = 0; i < targetPly; i++) {
      const node = tree.nodes[targetId];
      const nextId = node?.childrenIds?.[0];
      if (!nextId || !tree.nodes[nextId]) break;
      targetId = nextId;
    }

    tree = navigateTo(tree, targetId);

    setMoveTree(tree);
    setNavEpoch(n => n + 1);
    setPly(null);
    setSelectedSquare(null); setLegalDests({});
    setTreeViewActive(true);
    setAnalysisMode(true);
  }, [moveTree, ply, room?.moves, room?.starting_fen]);
  const exitAnalysis = useCallback(() => {
    setAnalysisMode(false);
    // Salir de Variantes también desengancha la auto-activación: no debe
    // reactivarse solo hasta que se cargue un nuevo ejercicio. Cancela pending.
    setExerciseLoaded(false);
    pendingAutoAnalysisFenRef.current = null;
    pendingAutoAnalysisPgnRef.current = null;
    officialNodeIdsRef.current = new Set();
    setSelectedSquare(null); setLegalDests({});
  }, []);
  // [Stage 3.1 · auto] Reutiliza EXACTAMENTE la secuencia de enterAnalysis pero
  // sembrando el árbol desde un ROOT explícito (el FEN del ejercicio recién
  // cargado), evitando depender del room.starting_fen aún no reconciliado.
  // Local y temporal: sin backend, sin tocar room.moves ni la solución oficial.
  const autoAnalysisFromExercise = useCallback(async (rootFen, pgn) => {
    const startingFen = rootFen || room?.starting_fen || STARTING_FEN;
    let tree = createTree(startingFen);
    const officialSet = new Set();
    // [Stage 3.2] Si el ejercicio trae PGN con variantes oficiales, sembrarlas
    // como ramas oficiales usando el addMove EXISTENTE (recursivo, preorden).
    if (pgn && pgn.trim()) {
      try {
        const { data } = await api.post('/pgn/variation-tree', { pgn });
        const children = Array.isArray(data?.children) ? data.children : [];
        if (children.length > 0) {
          tree = createTree(data?.root_fen || startingFen);
          const seed = (t, parentId, nodes) => {
            for (const n of nodes) {  // orden PGN → childrenIds[0] = mainline
              const t2 = addMove(t, parentId, {
                san: n.san, fen: n.fen_after, from: n.from, to: n.to,
                ...(n.promotion ? { promotion: n.promotion } : {}),
              });
              const childId = t2.currentId;      // addMove fija currentId al hijo
              officialSet.add(childId);          // registrar nodo OFICIAL
              t = seed(t2, childId, n.children || []); // continuaciones profundas
            }
            return t;
          };
          tree = seed(tree, tree.rootId, children);
          tree = navigateTo(tree, tree.rootId);  // volver al ROOT
        }
      } catch (e) {
        // Fallback: si el extractor falla, comportarse como Stage 3.1 (solo root).
        tree = createTree(startingFen);
        officialSet.clear();
      }
    }
    officialNodeIdsRef.current = officialSet;
    setMoveTree(tree);
    setExerciseLoaded(true);
    setNavEpoch(n => n + 1);
    setPly(null);
    setSelectedSquare(null); setLegalDests({});
    setTreeViewActive(true);
    setAnalysisMode(true);
  }, [room?.starting_fen]);
  // [Stage 3.1 · auto diferido] Consumidor del pending. Identidad estable via
  // ref (reasignado cada render con closure fresca) para poder invocarlo desde
  // el fetchRoom estable sin recrear el intervalo de polling. Consume UNA vez.
  maybeConsumeAutoAnalysisRef.current = (data) => {
    const pend = pendingAutoAnalysisFenRef.current;
    if (!pend || !isTeacher || !data) return;
    // Exclusiones: si la sala llegó a Capture/Puzzle/Explanation, cancelar.
    if (data.capture_overlay || data.active_puzzle || data.active_explanation) {
      pendingAutoAnalysisFenRef.current = null;
      pendingAutoAnalysisPgnRef.current = null;
      return;
    }
    // Aún no reconciliado por el servidor → esperar al siguiente poll.
    if (data.starting_fen !== pend) return;
    const pgn = pendingAutoAnalysisPgnRef.current;
    pendingAutoAnalysisFenRef.current = null; // guard anti-duplicados (1×)
    pendingAutoAnalysisPgnRef.current = null;
    autoAnalysisFromExercise(pend, pgn);
  };
  // Retroceder al padre (no destructivo — conserva todas las ramas).
  const analysisBack = useCallback(() => setMoveTree(t => {
    if (!t) return t;
    const p = getParent(t, t.currentId);
    if (!p || p.id === t.currentId) return t;   // no navega → no remount
    setNavEpoch(n => n + 1);
    return navigateTo(t, p.id);
  }), []);
  // Ir a una rama concreta (hijo del nodo actual).
  const analysisGoto = useCallback((nodeId) => {
    setMoveTree(t => {
      if (!t || nodeId === t.currentId) return t;
      setNavEpoch(n => n + 1);
      return navigateTo(t, nodeId);
    });
    setTreeViewActive(true);
    setPly(null);
  }, []);
  // Navegación natural desde el historial LIVE hacia el árbol persistente.
  // Convierte un ply de la mainline oficial en el nodo correspondiente.
  const analysisGotoPly = useCallback((targetPly) => {
    const startingFen = roomRef.current?.starting_fen || STARTING_FEN;
    const moves = roomRef.current?.moves || [];

    let tree = moveTree || createTree(startingFen);
    let nodeId = tree.rootId;

    const safePly = Math.max(0, Math.min(
      targetPly,
      moves.length
    ));

    for (let i = 0; i < safePly; i++) {
      const node = tree.nodes[nodeId];
      const nextId = node?.childrenIds?.[0];

      if (!nextId || !tree.nodes[nextId]) {
        return;
      }

      nodeId = nextId;
    }

    tree = navigateTo(tree, nodeId);

    setMoveTree(tree);
    setNavEpoch(n => n + 1);
    setPly(null);
    setSelectedSquare(null);
    setLegalDests({});
    setTreeViewActive(true);
  }, [moveTree]);
  analysisGotoPlyRef.current = analysisGotoPly;

  // ⏮ raíz · ▶️ primer hijo (mainline) del nodo actual · ⏭ punta de la mainline.
  const analysisFirst = useCallback(() => setMoveTree(t => {
    if (!t || t.rootId === t.currentId) return t;
    setNavEpoch(n => n + 1);
    return navigateTo(t, t.rootId);
  }), []);
  const analysisNext = useCallback(() => setMoveTree(t => {
    if (!t) return t;
    const cur = t.nodes[t.currentId];
    const firstChild = cur && cur.childrenIds && cur.childrenIds[0];
    if (!firstChild) return t;
    setNavEpoch(n => n + 1);
    return navigateTo(t, firstChild);
  }), []);
  const analysisLast = useCallback(() => setMoveTree(t => {
    if (!t) return t;
    let cursor = t.nodes[t.currentId];
    while (cursor && cursor.childrenIds && cursor.childrenIds[0]) {
      cursor = t.nodes[cursor.childrenIds[0]];
    }
    if (!cursor || cursor.id === t.currentId) return t;
    setNavEpoch(n => n + 1);
    return navigateTo(t, cursor.id);
  }), []);
  // Mantener el ref de navegación por teclado sincronizado con el estado actual.
  analysisNavRef.current = { treeViewActive, back: analysisBack, next: analysisNext };

  // [UX · árbol] Mantener visible la jugada seleccionada al navegar.
  // Solo afecta el panel del árbol de Classroom Live.
  useEffect(() => {
    if (!treeViewActive || !moveTree?.currentId) return;

    const node = document.querySelector(
      `[data-testid="analysis-tree-move-${moveTree.currentId}"]`
    );

    if (node) {
      node.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [treeViewActive, moveTree?.currentId]);

  // ── FASE D1 · Análisis compartido en tiempo real (Opción 2) ───────────────
  // Publica el FEN del nodo actual del árbol al Classroom vía el endpoint
  // EXISTENTE /view_ply (solo escribe room.fen; NO toca moves[]/solución). El
  // alumno lo recibe por el polling de 1s. moveTree sigue 100% LOCAL: nunca se
  // envía el árbol ni respuestas oficiales, solo el FEN del nodo actual.
  const lastPublishedFenRef = useRef(null);
  const liveFenBeforeAnalysisRef = useRef(null);
  const prevTreeViewActiveRef = useRef(false);
  const publishViewFen = useCallback((fen) => {
    if (!fen || !isTeacherRef.current) return;
    if (lastPublishedFenRef.current === fen) return;   // dedup: no re-publicar el mismo FEN
    lastPublishedFenRef.current = fen;
    api.post(`/classrooms/${id}/view_ply`, { fen }).catch(() => {});
  }, [id]);
  // Entrar/salir de la vista del árbol: captura la posición LIVE previa.
  // Salir de "Variantes" NO restaura LIVE: mientras treeViewActive siga true,
  // el nodo seleccionado continúa siendo la posición compartida.
  // LIVE se restaura explícitamente cuando goLive() desactiva treeViewActive.
  useEffect(() => {
    const was = prevTreeViewActiveRef.current;
    if (treeViewActive && !was) {
      liveFenBeforeAnalysisRef.current = room?.fen || null;
      lastPublishedFenRef.current = null;
    } else if (!treeViewActive && was) {
      // goLive() ya publica la posición LIVE real mediante syncViewPly(null).
      // Aquí solo liberamos el dedup para que el siguiente nodo/estado pueda
      // publicarse normalmente, sin competir con syncViewPly().
      lastPublishedFenRef.current = null;
    }
    prevTreeViewActiveRef.current = treeViewActive;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeViewActive]);
  // Publicar el FEN del nodo actual en cada cambio REAL de currentId (crear
  // jugada o navegar). Dedup por FEN evita requests por render/polling/remount.
  useEffect(() => {
    if (!treeViewActive || !moveTree || !isTeacherRef.current) return;
    const n = moveTree.nodes[moveTree.currentId];
    const fen = n ? (n.move ? n.move.fen : n.fen) : null;
    if (fen) publishViewFen(fen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeViewActive, moveTree?.currentId]);

  const boardFen = useMemo(() => {
    if (!room) return null;
    // [Stage 3.1] En modo Variantes el tablero refleja el nodo actual del árbol
    // (exploración local). No depende de room.fen ni de ply.
    if (treeViewActive && moveTree) {
      const n = moveTree.nodes[moveTree.currentId];
      return (n && (n.move ? n.move.fen : n.fen)) || room.fen;
    }
    const totalMoves = room.moves?.length || 0;
    // Live / after last move → current fen
    if (ply === null || ply >= totalMoves) return room.fen;
    // ply 0 → starting position of this session
    if (ply === 0) return room.starting_fen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
    // Preferred: use the FEN snapshot stored at that move (survives illegal moves & loaded positions)
    const snap = room.moves?.[ply - 1]?.fen;
    if (snap) return snap;
    // Fallback: replay strictly legal SANs from the session start
    try {
      const c = new Chess(room.starting_fen || undefined);
      for (let i = 0; i < ply; i++) {
        const m = room.moves[i];
        if (m?.fen) { c.load(m.fen); continue; }   // survive illegal moves in history
        c.move(m.san);
      }
      return c.fen();
    } catch { return room.fen; }
  }, [room, ply, analysisMode, moveTree, treeViewActive]);


  // Fix #5B — Estos 3 memos DEBEN declararse ANTES de cualquier early return
  // (líneas 857/866) para no violar las reglas de hooks. Sus dependencias
  // (room, ply, selectedSquare, legalDests) ya están declaradas arriba.
  //
  // 1) Last-move highlight for the main explanation board. Uses the real
  //    from/to that the backend already persists for every teacher move
  //    (`room.moves[i]`). While the teacher is navigating history via `ply`,
  //    highlight the move that led to the currently displayed position;
  //    when live, use the latest move.
  const lastMoveHighlight = useMemo(() => {
    let mv = null;

    if (treeViewActive && moveTree) {
      const currentNode = moveTree.nodes[moveTree.currentId];
      mv = currentNode?.move || null;
    } else {
      const list = room?.moves || [];
      if (list.length > 0) {
        const idx = ply === null ? list.length - 1 : Math.max(0, ply - 1);
        mv = list[idx] || null;
      }
    }

    if (!mv?.from || !mv?.to) return {};

    return {
      [mv.from]: { backgroundColor: 'rgba(255, 210, 63, 0.45)' },
      [mv.to]:   { backgroundColor: 'rgba(255, 210, 63, 0.55)' },
    };
  }, [room?.moves, ply, treeViewActive, moveTree]);

  // 2) Emoji overlay from a loaded Capture Activity (targets + obstacles).
  //    The student's chess piece is already in the FEN — we only paint the
  //    extras. NOTE: the square tint (red for targets, grey for obstacles)
  //    was removed so the teacher's board stays clean while explaining —
  //    only the emoji is drawn. Capture Activity gameplay for the STUDENT
  //    is untouched (CaptureActivityPlayer keeps its own styling logic).
  const captureOverlayStyles = useMemo(() => {
    const cfg = room?.capture_overlay;
    if (!cfg || !Array.isArray(cfg.pieces)) {
      return {};
    }
    const styles = {};
    for (const p of cfg.pieces) {
      if (!p?.square || p.role === 'student' || !p.emoji) continue;
      const safe = String(p.emoji).replace(/"/g, '');
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text x='50' y='55' text-anchor='middle' dominant-baseline='central' font-size='68'>${safe}</text></svg>`;
      styles[p.square] = {
        backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '78% 78%',
      };
    }
    return styles;
  }, [room?.capture_overlay]);

  // 3) Merge selection + legal-move hints with the existing last-move
  //    highlight. Depends on the two memos above and on `selectedSquare` /
  //    `legalDests` (local state controlling the click-hint).
  const boardSquareStyles = useMemo(() => {
    const out = { ...(captureOverlayStyles || {}), ...(lastMoveHighlight || {}) };
    if (selectedSquare) {
      out[selectedSquare] = { ...(out[selectedSquare] || {}), backgroundColor: 'rgba(255, 236, 100, 0.75)' };
    }
    Object.entries(legalDests).forEach(([dst, meta]) => {
      const base = out[dst] || {};
      // Unified visual language with Capture Activity: crisp defined ring
      // (88%-96%) around a capturable piece — object stays fully visible —
      // and a small centered dot on empty legal squares. Compose with any
      // pre-existing base backgroundImage (e.g. the Capture Activity emoji
      // painted by captureOverlayStyles) via CSS multi-background: the
      // gradient is listed FIRST so it paints ON TOP of the emoji, keeping
      // the object fully visible under the ring on target squares.
      const marker = meta.isCapture
        ? 'radial-gradient(circle, transparent 0 88%, rgba(0,0,0,0.42) 88% 96%, transparent 96% 100%)'
        : 'radial-gradient(circle, rgba(0,0,0,0.28) 0 18%, transparent 18% 100%)';
      const composedImage = base.backgroundImage
        ? `${marker}, ${base.backgroundImage}`
        : marker;
      out[dst] = { ...base, backgroundImage: composedImage };
    });
    return out;
  }, [captureOverlayStyles, lastMoveHighlight, selectedSquare, legalDests]);

  // -----------------------------------------------------------------------
  // Central move applier: EVERY move (drag, promotion, engine, illegal, PGN)
  // funnels here. State update is immutable and happens exactly once per call.
  // -----------------------------------------------------------------------
  const applyLocalMove = useCallback(({ san, fen, from, to }) => {
    setRoom(prev => {
      if (!prev) return prev;
      const nextMoves = [...(prev.moves || []), { san, fen, from, to }];
      optimisticLenRef.current = nextMoves.length;
      optimisticFenRef.current = fen;
      return { ...prev, fen, moves: nextMoves, arrows: [], circles: {} };
    });
    // Fire-and-forget POST. Server is the durable log; if it fails we keep local state.
    // NOTE: no fetchRoom() here — polling will reconcile once server catches up.
    api.post(`/classrooms/${id}/move`, { san, fen, from_sq: from, to_sq: to })
       .catch(() => toast.error('No se pudo sincronizar el movimiento con el servidor'));
  }, [id]);

  // Server-authoritative FEN reset (used by load/edit/reset).
  // Increments a monotonic version so any in-flight polls are ignored briefly.
  // `preserveOverlay` keeps a live Capture Activity overlay alive across moves
  // (backend clears the overlay only when capture_config is explicitly passed).
  const pushFen = useCallback(async (newFen, note, preserveOverlay) => {
    optimisticVersionRef.current += 1;
    optimisticLenRef.current = 0;
    optimisticFenRef.current = newFen;
    suppressPollUntilRef.current = Date.now() + 1500;
    setRoom(prev => prev ? ({ ...prev, fen: newFen, moves: [], arrows: [], circles: {} }) : prev);
    try {
      const body = { fen: newFen };
      if (preserveOverlay) body.capture_config = preserveOverlay;
      await api.post(`/classrooms/${id}/load`, body);
      if (note) toast.success(note);
    } catch { toast.error('Error al sincronizar tablero'); }
  }, [id]);

  const engineReply = async (currentFen) => {
    if (!engine.ready) { toast.error('Motor no listo'); return; }
    try {
      const uci = await engine.bestMove(currentFen, { level: engineLevel, movetime: 700 });
      if (!uci || uci === '(none)') return;
      const from = uci.slice(0, 2);
      const to = uci.slice(2, 4);
      const promo = uci.length > 4 ? uci[4] : 'q';
      const c = new Chess(currentFen);
      let mv;
      try { mv = c.move({ from, to, promotion: promo }); } catch { mv = null; }
      if (!mv) return;
      applyLocalMove({ san: mv.san, fen: c.fen(), from, to });
    } catch (e) { toast.error('Motor no respondió'); }
  };

  // Rook rays (used by the Load Game / Capture Activity board in the Classroom).
  // Obstacles block the ray; targets do NOT block — the piece can pass over
  // and land on them (which triggers the capture in onPieceDrop).
  const computeCaptureRookMoves = (from, obstacleSquares) => {
    const FILES = 'abcdefgh';
    const file = FILES.indexOf(from[0]);
    const rank = parseInt(from[1], 10) - 1;
    const obstacles = new Set(obstacleSquares || []);
    const out = new Set();
    const rays = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    for (const [df, dr] of rays) {
      let f = file + df, r = rank + dr;
      while (f >= 0 && f < 8 && r >= 0 && r < 8) {
        const sq = FILES[f] + (r + 1);
        if (obstacles.has(sq)) break;
        out.add(sq);
        f += df; r += dr;
      }
    }
    return out;
  };

  // Bishop rays — 4 diagonal directions. Same blocking semantics as the rook.
  const computeCaptureBishopMoves = (from, obstacleSquares) => {
    const FILES = 'abcdefgh';
    const file = FILES.indexOf(from[0]);
    const rank = parseInt(from[1], 10) - 1;
    const obstacles = new Set(obstacleSquares || []);
    const out = new Set();
    const rays = [[1, 1], [1, -1], [-1, 1], [-1, -1]];
    for (const [df, dr] of rays) {
      let f = file + df, r = rank + dr;
      while (f >= 0 && f < 8 && r >= 0 && r < 8) {
        const sq = FILES[f] + (r + 1);
        if (obstacles.has(sq)) break;
        out.add(sq);
        f += df; r += dr;
      }
    }
    return out;
  };

  // Knight jumps — obstacles between origin and destination are IGNORED (the
  // knight jumps over them). Landing on an obstacle is still forbidden so the
  // knight cannot occupy a wall square.
  const computeCaptureKnightMoves = (from, obstacleSquares) => {
    const FILES = 'abcdefgh';
    const file = FILES.indexOf(from[0]);
    const rank = parseInt(from[1], 10) - 1;
    const obstacles = new Set(obstacleSquares || []);
    const offsets = [[1, 2], [2, 1], [2, -1], [1, -2], [-1, -2], [-2, -1], [-2, 1], [-1, 2]];
    const out = new Set();
    for (const [df, dr] of offsets) {
      const f = file + df, r = rank + dr;
      if (f < 0 || f >= 8 || r < 0 || r >= 8) continue;
      const sq = FILES[f] + (r + 1);
      if (obstacles.has(sq)) continue;
      out.add(sq);
    }
    return out;
  };

  // Queen = rook ∪ bishop. Same ray-blocking semantics.
  const computeCaptureQueenMoves = (from, obstacleSquares) => {
    const out = new Set();
    computeCaptureRookMoves(from, obstacleSquares).forEach(sq => out.add(sq));
    computeCaptureBishopMoves(from, obstacleSquares).forEach(sq => out.add(sq));
    return out;
  };

  // Drag-and-drop validation: sliding pieces (R/B/Q) cannot end their move
  // BEYOND a target that sits between origin and destination on the ray. If
  // any intermediate square contains a target the whole move is rejected —
  // the student must walk to that target explicitly to capture it. Returns
  // true when a target sits strictly between `from` and `to` (excluding both).
  const pathHasBlockingTarget = (from, to, targetSquares) => {
    const FILES = 'abcdefgh';
    const fx = FILES.indexOf(from[0]);
    const fy = parseInt(from[1], 10) - 1;
    const tx = FILES.indexOf(to[0]);
    const ty = parseInt(to[1], 10) - 1;
    const dx = tx - fx, dy = ty - fy;
    const isOrtho = (dx === 0) !== (dy === 0);
    const isDiag = Math.abs(dx) === Math.abs(dy) && dx !== 0;
    if (!isOrtho && !isDiag) return false;
    const sx = Math.sign(dx), sy = Math.sign(dy);
    const steps = Math.max(Math.abs(dx), Math.abs(dy));
    const targets = new Set(targetSquares || []);
    for (let i = 1; i < steps; i++) { // strictly between — excludes `to`
      const x = fx + sx * i, y = fy + sy * i;
      const sq = FILES[x] + (y + 1);
      if (targets.has(sq)) return true;
    }
    return false;
  };

  // King: exactly one square in any of the 8 directions. Obstacles block —
  // the king cannot land on a wall.
  const computeCaptureKingMoves = (from, obstacleSquares) => {
    const FILES = 'abcdefgh';
    const file = FILES.indexOf(from[0]);
    const rank = parseInt(from[1], 10) - 1;
    const obstacles = new Set(obstacleSquares || []);
    const offsets = [[-1, -1], [-1, 0], [-1, 1], [0, -1], [0, 1], [1, -1], [1, 0], [1, 1]];
    const out = new Set();
    for (const [df, dr] of offsets) {
      const f = file + df, r = rank + dr;
      if (f < 0 || f >= 8 || r < 0 || r >= 8) continue;
      const sq = FILES[f] + (r + 1);
      if (obstacles.has(sq)) continue;
      out.add(sq);
    }
    return out;
  };

  // Pawn: color-aware. Forward advance 1 square if empty; 2 squares only from
  // starting rank if both squares are empty. Diagonal capture ONLY if the
  // diagonal-forward square contains a target (not an obstacle). Pawns cannot
  // capture forward — a target directly in front blocks the advance instead.
  const computeCapturePawnMoves = (from, color, obstacleSquares, targetSquares) => {
    const FILES = 'abcdefgh';
    const file = FILES.indexOf(from[0]);
    const rank = parseInt(from[1], 10) - 1;
    const dir = color === 'w' ? 1 : -1;
    const startRank = color === 'w' ? 1 : 6; // rank 2 (index 1) or rank 7 (index 6)
    const obstacles = new Set(obstacleSquares || []);
    const targets = new Set(targetSquares || []);
    const blockers = new Set([...obstacles, ...targets]);
    const out = new Set();
    // Forward one
    const r1 = rank + dir;
    if (r1 >= 0 && r1 < 8) {
      const s1 = FILES[file] + (r1 + 1);
      if (!blockers.has(s1)) {
        out.add(s1);
        // Forward two — only from starting rank and only if both squares clear
        if (rank === startRank) {
          const r2 = rank + 2 * dir;
          if (r2 >= 0 && r2 < 8) {
            const s2 = FILES[file] + (r2 + 1);
            if (!blockers.has(s2)) out.add(s2);
          }
        }
      }
    }
    // Diagonal capture — only if a target sits on the diagonal-forward square
    for (const df of [-1, 1]) {
      const f = file + df;
      if (f < 0 || f >= 8) continue;
      const r = rank + dir;
      if (r < 0 || r >= 8) continue;
      const sq = FILES[f] + (r + 1);
      if (targets.has(sq)) out.add(sq);
    }
    return out;
  };

  // ═══════════════════════════════════════════════════════════════════════
  // Paso 1.2 (Fix perf drag pickup) — Identidad estable para onPieceDrop
  // ═══════════════════════════════════════════════════════════════════════
  // Motivación: onPieceDrop se pasa como prop a <ChessBoardView>. Cuando su
  // identidad cambia entre renders, <ChessBoardView> recomputa `options`, y
  // <Chessboard> (react-chessboard@5.12 sobre @dnd-kit) recibe props nuevas
  // durante la ventana de activation del PointerSensor. Ese reset del
  // sensor obliga al profesor a "presionar dos veces" la pieza en Capture
  // Activity.
  //
  // Patrón elegido (industry standard "latest-ref callback"):
  //   1. `onPieceDropLatestRef` guarda la implementación viva. Se reasigna
  //      DURANTE el render con la closure más fresca (todas las variables
  //      de estado y helpers del render actual) — equivalente a listar
  //      TODAS las dependencias en useCallback, sin el riesgo de omitir
  //      alguna ni de invalidar la memoización en cada render.
  //   2. `onPieceDrop` es un useCallback con deps [] → identidad estable
  //      de por vida. Delega al ref.
  //
  // Ventaja vs useCallback([room, illegalMode, ply, isTeacher, vsEngine,
  //   engineSide, applyLocalMove, pushFen, computeCaptureRookMoves,
  //   computeCaptureBishopMoves, computeCaptureKnightMoves,
  //   computeCaptureQueenMoves, computeCaptureKingMoves,
  //   computeCapturePawnMoves, pathHasBlockingTarget, engineReply, ...]):
  //   los helpers `computeCaptureXxxMoves` y `pathHasBlockingTarget` están
  //   declarados en el cuerpo del componente y cambian de identidad en
  //   cada render — listarlos como deps del useCallback lo invalidaría en
  //   cada render, defeat-eando el propósito de este paso. El patrón
  //   latest-ref los captura por closure fresca sin ese problema.
  //
  // Lógica interna de onPieceDrop: 100% verbatim, cero cambio funcional.
  // ═══════════════════════════════════════════════════════════════════════
  const onPieceDropLatestRef = useRef(null);
  onPieceDropLatestRef.current = (from, to) => {
    // [Stage 3.1] Modo Variantes: jugada LOCAL sobre el árbol. Sin backend, sin
    // sincronización, sin tocar room.moves. Deduplica vía addMove de moveTree.js.
    if (isTeacher && treeViewActive && moveTree) {
      const node = moveTree.nodes[moveTree.currentId];
      const baseFen = node && (node.move ? node.move.fen : node.fen);
      if (!baseFen) return false;
      let c, mv;
      try { c = new Chess(baseFen); mv = c.move({ from, to, promotion: 'q' }); }
      catch { return false; }
      if (!mv) return false;
      setMoveTree(t => addMove(t, t.currentId, {
        san: mv.san,
        fen: c.fen(),
        from,
        to,
        ...(mv.promotion ? { promotion: mv.promotion } : {}),
      }));
      return true;
    }
    // Guardrails: only teacher, live view, no concurrent move in flight
    if (!isTeacher || ply !== null || treeViewActive) { return false; }
    if (moveLockRef.current) { return false; }
    moveLockRef.current = true;

    try {
      // CAPTURE ACTIVITY OVERLAY MODE: The room has a Capture Activity loaded
      // (student piece + emoji targets/obstacles). Kings may be missing and the
      // position is NOT a legal chess game, so we bypass chess.js entirely.
      // We validate the move ourselves using the piece-type rules and the
      // obstacle set from the overlay, and we mutate the overlay so captured
      // targets disappear immediately.
      if (room?.capture_overlay) {
        const cfg = room.capture_overlay;
        const piecesArr = Array.isArray(cfg.pieces) ? cfg.pieces : [];
        const student = piecesArr.find(p => p.role === 'student');
        if (!student) return false;
        // Only the student's piece may be dragged
        if (from !== student.square) return false;
        const obstacleSquares = piecesArr.filter(p => p.role === 'obstacle').map(p => p.square);
        const targetSquares = piecesArr.filter(p => p.role === 'target').map(p => p.square);
        // Piece-specific move validation. Rook, Bishop, Knight, Queen, King
        // and Pawn implemented. Other pieces silently reject.
        let legal;
        if (student.piece === 'R') legal = computeCaptureRookMoves(from, obstacleSquares);
        else if (student.piece === 'B') legal = computeCaptureBishopMoves(from, obstacleSquares);
        else if (student.piece === 'N') legal = computeCaptureKnightMoves(from, obstacleSquares);
        else if (student.piece === 'Q') legal = computeCaptureQueenMoves(from, obstacleSquares);
        else if (student.piece === 'K') legal = computeCaptureKingMoves(from, obstacleSquares);
        else if (student.piece === 'P') legal = computeCapturePawnMoves(from, student.color, obstacleSquares, targetSquares);
        else legal = new Set();
        // Sliding pieces cannot end their move BEYOND a target on the ray —
        // the whole move is rejected. Landing exactly on the target is fine
        // (that is the capture). Knight jumps and King one-square moves are
        // never affected because they have no traversable intermediate squares.
        if (['R', 'B', 'Q'].includes(student.piece)) {
          if (pathHasBlockingTarget(from, to, targetSquares)) return false;
        }
        if (!legal.has(to)) return false;
        // Detect a capture BEFORE mutating the array so we can play the reward
        // sound only when the piece actually lands on a target square.
        const didCapture = piecesArr.some(p => p.role === 'target' && p.square === to);
        // Move the piece by rewriting the FEN placement (no chess.js).
        const newFen = applyIllegalMove(room.fen, from, to, true);
        if (!newFen) return false;
        // Update the overlay: student square advances, and any target sitting
        // on the destination square is removed (captured). Obstacles remain.
        const newPieces = piecesArr.flatMap(p => {
          if (p.role === 'target' && p.square === to) return []; // captured
          if (p.role === 'student') return [{ ...p, square: to }];
          return [p];
        });
        const newOverlay = {
          ...cfg,
          pieces: newPieces,
          student_square: to,
          target_squares: (cfg.target_squares || []).filter(sq => sq !== to),
        };
        // Optimistic local update — overlay change is visible instantly so the
        // captured emoji vanishes on the same frame as the drop. pushFen's own
        // setRoom then updates fen/moves/arrows/circles, preserving the overlay
        // change because it spreads prev state (React batches both updates).
        setRoom(prev => prev ? { ...prev, capture_overlay: newOverlay } : prev);
        if (didCapture) playCaptureSound();
        pushFen(newFen, null, newOverlay);
        return true;
      }

      // ILLEGAL MOVES MODE: manipulate FEN directly, no validation, keep turn.
      // In illegal mode we bypass move-list reconciliation entirely (pushFen resets moves).
      if (illegalMode) {
        const newFen = applyIllegalMove(room.fen, from, to, true);
        if (!newFen) return false;
        pushFen(newFen);
        return true;
      }

      // Read fen from state at THIS instant (avoid stale closures)
      const currentFen = optimisticFenRef.current || room.fen;

      // Detect promotion: pawn reaching last rank
      const c0 = new Chess(currentFen);
      const src = c0.get(from);
      if (src && src.type === 'p' &&
          ((src.color === 'w' && to[1] === '8') || (src.color === 'b' && to[1] === '1'))) {
        setPendingPromotion({ from, to, color: src.color });
        return false;
      }

      const c = new Chess(currentFen);
      let mv;
      try { mv = c.move({ from, to, promotion: 'q' }); } catch (err) {
        return false;
      }
      if (!mv) {
        return false;
      }
      const newFen = c.fen();
      applyLocalMove({ san: mv.san, fen: newFen, from, to });
      if (vsEngine && c.turn() === engineSide) setTimeout(() => engineReply(newFen), 150);
      return true;
    } finally {
      // Release lock next tick so the same drop event can't fire twice
      setTimeout(() => { moveLockRef.current = false; }, 0);
    }
  };
  // Paso 1.2 — Wrapper con identidad estable. useCallback con deps [] →
  // esta función NUNCA cambia de identidad → <ChessBoardView> nunca recibe
  // una prop `onPieceDrop` nueva → el memo `options` no se invalida por
  // este motivo → <Chessboard> no re-registra sensores de @dnd-kit durante
  // la activation del drag.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onPieceDrop = useCallback((from, to) => {
    return onPieceDropLatestRef.current ? onPieceDropLatestRef.current(from, to) : false;
  }, []);

  const applyPromotion = (piece) => {
    if (!pendingPromotion) return;
    const { from, to } = pendingPromotion;
    setPendingPromotion(null);
    if (moveLockRef.current) return;
    moveLockRef.current = true;
    try {
      const currentFen = optimisticFenRef.current || room.fen;
      const c = new Chess(currentFen);
      let mv;
      try { mv = c.move({ from, to, promotion: piece }); } catch { mv = null; }
      if (!mv) { toast.error('Coronación no válida'); return; }
      const newFen = c.fen();
      applyLocalMove({ san: mv.san, fen: newFen, from, to });
      if (vsEngine && c.turn() === engineSide) setTimeout(() => engineReply(newFen), 150);
    } finally {
      setTimeout(() => { moveLockRef.current = false; }, 0);
    }
  };

  const annTimerRef = useRef(null);
  const annVersionRef = useRef(0);
  const onAnnotationsChange = useCallback((arrows, circles) => {
    if (!isTeacher) return;
    // Bump the version FIRST so any in-flight POST callback that resolves
    // after this call sees a newer version and refuses to touch the pending
    // flag — kills the race where an old POST's .then() would clear
    // _pendingAnnots while a newer local annotation was still un-persisted.
    annVersionRef.current += 1;
    const myVersion = annVersionRef.current;
    setRoom(prev => ({ ...prev, arrows, circles, _pendingAnnots: true }));
    if (annTimerRef.current) clearTimeout(annTimerRef.current);
    annTimerRef.current = setTimeout(() => {
      api.post(`/classrooms/${id}/annotations`, { arrows, circles })
        .then(() => setRoom(prev => {
          if (!prev) return prev;
          // Only the LATEST scheduled write is allowed to lower the flag.
          if (myVersion !== annVersionRef.current) return prev;
          return { ...prev, _pendingAnnots: false };
        }))
        .catch(() => toast.error('No se pudieron guardar las flechas'));
    }, 250);
  }, [id, isTeacher]);

  const clearAnnotations = useCallback(() => onAnnotationsChange([], {}), [onAnnotationsChange]);

  const sendChat = async (e) => {
    e?.preventDefault?.();
    if (!chatText.trim()) return;
    const text = chatText.trim();
    setChatText('');
    try { await api.post(`/classrooms/${id}/chat`, { text }); fetchRoom(); }
    catch { toast.error('Error enviando mensaje'); }
  };

  // E2 · "Salir" = navegación pura. Backend /leave es NO-OP desde E1, así que
  // ya no lo llamamos: solo abandonamos la pantalla conservando la membresía.
  const exitRoom = () => {
    nav(user?.role === 'student' ? '/student/classrooms' : '/teacher/classrooms');
  };

  // E2 · "Terminar clase" = cierre explícito (solo canManage). El recap y el
  // cambio active=false/ended_at los realiza el backend E1 (POST /end).
  const endClass = async () => {
    setEndingClass(true);
    try {
      await api.post(`/classrooms/${id}/end`);
      await fetchRoom();
      toast.success('Clase terminada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo terminar la clase');
    } finally {
      setEndingClass(false);
      setEndDialogOpen(false);
    }
  };

  const resetSession = async () => {
    if (!isTeacher) return;
    if (!window.confirm('¿Iniciar una nueva sesión? Todos los alumnos volverán a 0 puntos.')) return;
    try {
      await api.post(`/classrooms/${id}/session/reset`);
      toast.success('Nueva sesión iniciada · 0 puntos para todos');
      fetchRoom();
    } catch { toast.error('No se pudo reiniciar la sesión'); }
  };

  const toggleMovesVisible = async () => {
    await api.patch(`/classrooms/${id}`, { moves_visible: !room.moves_visible });
    fetchRoom();
  };

  const reset = async () => {
    if (!isTeacher) return;
    optimisticVersionRef.current += 1;
    optimisticLenRef.current = 0;
    optimisticFenRef.current = null;
    suppressPollUntilRef.current = Date.now() + 1500;
    setSelectedSquare(null); setLegalDests({});
    // Reset means "abandonar completamente el ejercicio y arrancar en la
    // posición inicial estándar". Exit any navigation, clear solution and
    // capture overlay, and refresh the ply=0 reference so ◀️ can no longer
    // return to the previous exercise's starting position.
    const START = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

    setPly(null);
    setTreeViewActive(false);
    setMoveTree(createTree(START));
    // Volver a "en vivo" desactiva la auto-Variantes por completo + cancela pending.
    setExerciseLoaded(false); setAnalysisMode(false);
    pendingAutoAnalysisFenRef.current = null;
    pendingAutoAnalysisPgnRef.current = null;
    officialNodeIdsRef.current = new Set();
    setRoom(prev => prev ? ({
      ...prev,
      fen: START,
      starting_fen: START,
      moves: [], arrows: [], circles: {},
      solution_moves: null,
      capture_overlay: null,
    }) : prev);
    await api.post(`/classrooms/${id}/load`, {
      fen: null,
      solution_moves: [],
      is_new_exercise: true,
    });
    fetchRoom();
  };

  const undo = async () => {
    if (!isTeacher || !room?.moves?.length) return;
    const newMoves = room.moves.slice(0, -1);
    const c = new Chess();
    for (const m of newMoves) c.move(m.san);
    optimisticVersionRef.current += 1;
    optimisticLenRef.current = 0;
    optimisticFenRef.current = null;
    suppressPollUntilRef.current = Date.now() + 1500;
    setSelectedSquare(null); setLegalDests({});
    await api.post(`/classrooms/${id}/load`, { fen: c.fen() });
    // Also drop moves array
    await api.patch(`/classrooms/${id}`, {});
    // Load endpoint already resets moves array; we need to preserve them - use direct load with pgn
    // For MVP: keep it simple, load clears moves
    fetchRoom();
  };

  const loadContent = async () => {
    if (!isTeacher) return;
    try {
      // Reset optimistic refs so the next move reads room.fen fresh (same
      // reason as loadGameToBoard: avoid stale kingless FEN carrying over).
      optimisticVersionRef.current += 1;
      optimisticLenRef.current = 0;
      optimisticFenRef.current = null;
      suppressPollUntilRef.current = Date.now() + 1500;
      setSelectedSquare(null); setLegalDests({});

      const manualPgn = pgnInput.trim();
      const manualFen = fenInput.trim() || null;

      let manualRootFen = manualFen;

      // Obtener el FEN raíz real del PGN, incluyendo partidas con etiqueta FEN.
      // El extractor también se reutiliza después para reconstruir las variantes.
      if (manualPgn) {
        const { data: variationData } = await api.post(
          '/pgn/variation-tree',
          { pgn: manualPgn }
        );
        manualRootFen = variationData?.root_fen || null;
        if (!manualRootFen) {
          throw new Error('No se pudo obtener la posición inicial del PGN.');
        }
      }

      await api.post(`/classrooms/${id}/load`, {
        fen: manualFen,
        pgn: manualPgn || null,
        is_new_exercise: true
      });

      // Registrar el análisis después de cargar; fetchRoom consumirá el
      // pendiente cuando el servidor confirme el starting_fen correspondiente.
      pendingAutoAnalysisFenRef.current = manualPgn ? manualRootFen : null;
      pendingAutoAnalysisPgnRef.current = manualPgn || null;
      setExerciseLoaded(!!manualPgn);

      toast.success('Cargado');
      setOpenLoad(false); setPgnInput(''); setFenInput('');
      fetchRoom();
    } catch { toast.error('Error'); }
  };

  const copyCode = () => { if (room) { navigator.clipboard.writeText(room.code); toast.success('Código copiado'); } };
  const toggleVideo = async () => {
    try {
      const { data } = await api.post(`/classrooms/${id}/video`);
      toast.success(data.video_url ? 'Videollamada iniciada' : 'Videollamada terminada');
      fetchRoom();
    } catch { toast.error('Error'); }
  };
  // [FIX-DB-PERF] latest-ref: la implementación viva se reasigna en cada
  // render (closure fresca); el wrapper estable useCallback([]) se define
  // más abajo. Así `onLoadGame` no cambia de identidad y React.memo de
  // ClassroomMaterial funciona (no re-renderiza las miniaturas por cada move).
  const loadGameToBoardLatestRef = useRef(null);
  loadGameToBoardLatestRef.current = async (game) => {
    try {
      // Reset any stale optimistic state from a prior session so the next drag
      // does not pick up a FEN from a previous (possibly kingless) capture
      // activity. Without this, `currentFen = optimisticFenRef.current || room.fen`
      // in onPieceDrop keeps returning the old FEN even after Load Game switches
      // the classroom to a fresh normal exercise, and chess.js throws
      // "Invalid FEN: missing white king" on the very next move.
      optimisticVersionRef.current += 1;
      optimisticLenRef.current = 0;
      suppressPollUntilRef.current = Date.now() + 1500;
      setSelectedSquare(null); setLegalDests({});
      // Fix #4 — señal one-shot: el próximo commit del FEN es un cambio
      // brusco de posición (Load Game), NO una jugada incremental. Se
      // batchea junto al setRoom siguiente para que el board reciba
      // simultáneamente el nuevo FEN + animación desactivada, evitando
      // los ~180 ms de transición visual sin afectar movimientos normales.
      setSkipBoardAnim(true);
      // Loading a new exercise semantically means "go live" — clear any stale
      // history-navigation ply that a previous ← / ArrowLeft or move-list click
      // may have left behind. Without this, the board repaints correctly but
      // arePiecesDraggable / onSquareClick (both gated on ply === null) stay
      // false and pieces silently reject drops until a full page reload.
      // Mirrors the same reset that reset() already applies (line ~589).
      setPly(null);
      if (game.fen && !game.id) {
        // OPTIMISTIC REPAINT — update state locally BEFORE the network call so
        // the board shows the new position instantly. The polling loop will
        // reconcile any minor server-side details in the background.
        optimisticFenRef.current = game.fen;
        setRoom(prev => prev ? ({
          ...prev,
          fen: game.fen,
          // Also refresh the ply=0 reference locally so the ← / ◀️ button
          // returns to the EXACT position the exercise starts from, without
          // waiting for the /load network call + poll reconciliation. The
          // backend `/classrooms/{id}/load` mirrors this update server-side.
          starting_fen: game.fen,
          moves: [], arrows: [], circles: {},
          capture_overlay: game.capture_config || null,
          solution_moves: game.solution_moves || null,
        }) : prev);
        // [Stage 3.1 · auto diferido] NO activar aquí (montaría el sensor en
        // plena tormenta de re-render de la carga). Registrar el FEN pendiente;
        // se consume en el 1er poll del servidor ya reconciliado (fetchRoom).
        if (game.capture_config) {
          pendingAutoAnalysisFenRef.current = null;
          pendingAutoAnalysisPgnRef.current = null;
          setExerciseLoaded(false); setAnalysisMode(false);
        } else {
          pendingAutoAnalysisFenRef.current = game.fen;
          pendingAutoAnalysisPgnRef.current = game.pgn || null;
          setExerciseLoaded(true);
        }
        toast.success('Cargado al tablero');
        // Optional capture overlay + preloaded solution piggyback on the same
        // load endpoint so the whole classroom (and late joiners) sees them.
        api.post(`/classrooms/${id}/load`, {
          fen: game.fen,
          capture_config: game.capture_config || null,
          solution_moves: game.solution_moves || null,
          is_new_exercise: true,
        })
          .catch(() => toast.error('No se pudo sincronizar la carga con el servidor'));
      } else if (game.id) {
        const { data } = await api.get(`/games/${game.id}`);
        // Optimistic mirror of Branch A: when the game exposes a starting FEN,
        // repaint the classroom board LOCALLY before the POST so the teacher
        // sees the new position instantly and any drag/click immediately after
        // Load Game validates against the correct FEN (not the previous one).
        // PGN-only games (no starting_fen) keep the previous behaviour — the
        // board updates once the POST + fetchRoom cycle completes, because we
        // don't have a single FEN to paint until the server replays the PGN.
        if (data.starting_fen) {
          optimisticFenRef.current = data.starting_fen;
          setRoom(prev => prev ? ({
            ...prev,
            fen: data.starting_fen,
            starting_fen: data.starting_fen,
            moves: [],
            arrows: [],
            circles: {},
            capture_overlay: null,
            solution_moves: data.solution_moves || null,
          }) : prev);
          // [Stage 3.1 · auto diferido] Registrar pending; se consume tras la
          // reconciliación del servidor (fetchRoom), no de forma síncrona.
          pendingAutoAnalysisFenRef.current = data.starting_fen;
          pendingAutoAnalysisPgnRef.current = data.pgn || null;
          setExerciseLoaded(true);
        } else {
          optimisticFenRef.current = null;
        }
        await api.post(`/classrooms/${id}/load`, { pgn: data.pgn || null, fen: data.pgn ? null : (data.starting_fen || null), is_new_exercise: true });
        toast.success('Cargado al tablero');
        fetchRoom();
      }
    } catch { toast.error('No se pudo cargar'); }
  };
  // [FIX-DB-PERF] Props estables para ClassroomMaterial → React.memo activo →
  // las 50 miniaturas react-chessboard dejan de re-renderizarse en cada
  // movimiento/poll (postMs vuelve de ~2s a ~130ms).
  const loadGameToBoard = useCallback((game) => (
    loadGameToBoardLatestRef.current ? loadGameToBoardLatestRef.current(game) : undefined
  ), []);
  const handleLaunchExercise = useCallback((payload) => setLaunchCfg({
    speed_bonus: false, max_attempts: 3, ...payload,
    points: payload.points || 10, duration_seconds: payload.duration_seconds || 60,
  }), []);
  const handleHideMaterial = useCallback(() => setShowMaterial(false), []);
  // ── C2 · Control del profesor sobre la Explanation dirigida ──────────────
  // start → escribe active_explanation (backend limpia active_puzzle).
  // step  → cambia current_step (fuente de verdad = servidor). Optimista local
  //         con una ventana corta de supresión de poll para UX inmediata.
  // stop  → borra active_explanation.
  const startExplanation = useCallback(async (studyId, activityId) => {
    if (!studyId || !activityId) return;
    try {
      const { data } = await api.post(`/classrooms/${id}/explanation/start`, { study_id: studyId, activity_id: activityId });
      suppressPollUntilRef.current = Date.now() + 800;
      setRoom(prev => prev ? { ...prev, active_explanation: data, active_puzzle: null } : prev);
      setShowMaterial(false);
      toast.success('Clase de explicación iniciada');
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo iniciar la explicación'); }
  }, [id]);
  const setExplanationStep = useCallback(async (newStep) => {
    suppressPollUntilRef.current = Date.now() + 800;
    setRoom(prev => (prev && prev.active_explanation)
      ? { ...prev, active_explanation: { ...prev.active_explanation, current_step: newStep } }
      : prev);
    try { await api.post(`/classrooms/${id}/explanation/step`, { current_step: newStep }); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo cambiar el paso'); }
  }, [id]);
  // Navegación entre ejercicios independientes de Lava Floor.
  // No modifica el flujo de pasos de las explicaciones normales.
  const setLavaExercise = useCallback(async (newIndex) => {
    const active = room?.active_explanation;
    const total = Number(active?.total_lava_activities) || 1;
    if (!active || active.activity?.type !== 'lava_floor') return;
    if (newIndex < 0 || newIndex >= total) return;

    try {
      const { data } = await api.post(
        `/classrooms/${id}/explanation/lava-exercise`,
        { activity_index: newIndex }
      );
      suppressPollUntilRef.current = Date.now() + 800;
      setRoom(prev => prev
        ? { ...prev, active_explanation: data, active_puzzle: null }
        : prev
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar de ejercicio');
    }
  }, [id, room?.active_explanation]);

  const stopExplanation = useCallback(async () => {
    suppressPollUntilRef.current = Date.now() + 800;
    setRoom(prev => prev ? { ...prev, active_explanation: null } : prev);
    try { await api.post(`/classrooms/${id}/explanation/stop`); toast.success('Explicación finalizada'); }
    catch { toast.error('No se pudo finalizar la explicación'); }
  }, [id]);
  const doLaunch = async () => {
    if (!launchCfg) return;
    try {
      const body = {
        fen: launchCfg.fen, title: launchCfg.title, solution_moves: launchCfg.solution_moves || [],
        accepted_solutions: launchCfg.accepted_solutions || null,
        points: Number(launchCfg.points) || 10, duration_seconds: Number(launchCfg.duration_seconds) || 60,
        speed_bonus: !!launchCfg.speed_bonus, max_attempts: Number(launchCfg.max_attempts) || 3,
        capture_config: launchCfg.capture_config || null,
        instructions: launchCfg.instructions || '',
      };
      // Fase PC-5A: si viene desde una actividad play_computer, propagar
      // type + study_id + activity_id + config del motor al payload.
      if (launchCfg.type === 'play_computer') {
        body.type = 'play_computer';
        body.study_id = launchCfg.study_id;
        body.activity_id = launchCfg.activity_id;
        body.goal = launchCfg.goal || 'win';
        body.student_side = launchCfg.student_side || 'white';
        body.engine_level = Number.isFinite(launchCfg.engine_level) ? launchCfg.engine_level : 5;
      }
      await api.post(`/classrooms/${id}/puzzle`, body);
      toast.success('Ejercicio lanzado a los alumnos');
      setLaunchCfg(null); fetchRoom();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al lanzar'); }
  };
  const kickParticipant = async (uid) => {
    if (!window.confirm('¿Expulsar de la sala?')) return;
    await api.delete(`/classrooms/${id}/participants/${uid}`);
    toast.success('Participante expulsado'); fetchRoom();
  };
  const toggleMute = async (uid, muted) => {
    await api.post(`/classrooms/${id}/mute`, { user_id: uid, muted });
    toast.success(muted ? 'Silenciado' : 'Voz devuelta'); fetchRoom();
  };
  const raiseHand = async () => {
    try { await api.post(`/classrooms/${id}/hand`); toast.success('Levantaste la mano ✋'); fetchRoom(); }
    catch { toast.error('Error'); }
  };
  const dismissHand = async (uid) => {
    await api.delete(`/classrooms/${id}/hand/${uid}`);
    fetchRoom();
  };
  const setAttendance = async (uid, status) => {
    try {
      await api.post(`/classrooms/${id}/attendance`, {
        user_id: uid,
        status
      });
      fetchRoom();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar asistencia');
    }
  };
  const downloadPgn = () => {
    if (!room?.moves?.length) { toast.error('Sin movimientos'); return; }
    const c = new Chess();
    for (const m of room.moves) { try { c.move(m.san); } catch {} }
    const blob = new Blob([c.pgn()], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${room.name.replace(/\s+/g,'_')}.pgn`; a.click();
    URL.revokeObjectURL(url);
  };

  if (error) return (
    <div className="min-h-screen grid place-items-center">
      <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center max-w-md">
        <div className="font-display text-2xl font-extrabold mb-2">No puedes entrar</div>
        <div className="text-slate-500 mb-4">{error}</div>
        <Button onClick={() => nav(-1)}>Volver</Button>
      </div>
    </div>
  );
  if (!room) return <div className="p-12">Cargando salón…</div>;

  // Click-to-move for the teacher's main board. Uses chess.js (the same engine
  // that validates local moves) to compute legal destinations and funnels every
  // accepted click through the existing onPieceDrop() pipeline. Drag & drop and
  // right-click annotations continue to work unchanged.
  const computeLegalDests = (sq) => {
    if (!boardFen) return {};
    // Capture Activity: the position may not be a legal chess game (no king),
    // so chess.js can't compute moves. Reuse the same ray-based helpers that
    // onPieceDrop already uses to validate drag & drop — this guarantees the
    // click-to-move indicators match the drag validation 1:1.
    if (room?.capture_overlay) {
      const cfg = room.capture_overlay;
      const piecesArr = Array.isArray(cfg.pieces) ? cfg.pieces : [];
      const student = piecesArr.find(p => p.role === 'student');
      if (!student || sq !== student.square) return {};
      const obstacleSquares = piecesArr.filter(p => p.role === 'obstacle').map(p => p.square);
      const targetSquares  = piecesArr.filter(p => p.role === 'target').map(p => p.square);
      let legal;
      if (student.piece === 'R') legal = computeCaptureRookMoves(sq, obstacleSquares);
      else if (student.piece === 'B') legal = computeCaptureBishopMoves(sq, obstacleSquares);
      else if (student.piece === 'N') legal = computeCaptureKnightMoves(sq, obstacleSquares);
      else if (student.piece === 'Q') legal = computeCaptureQueenMoves(sq, obstacleSquares);
      else if (student.piece === 'K') legal = computeCaptureKingMoves(sq, obstacleSquares);
      else if (student.piece === 'P') legal = computeCapturePawnMoves(sq, student.color, obstacleSquares, targetSquares);
      else legal = new Set();
      const targetSet = new Set(targetSquares);
      const out = {};
      legal.forEach(dst => {
        // Sliding pieces: a target strictly between origin and dst blocks the
        // whole move (identical to onPieceDrop line 446–448). Landing exactly
        // on a target is fine — that IS the capture.
        if (['R','B','Q'].includes(student.piece) && pathHasBlockingTarget(sq, dst, targetSquares)) return;
        out[dst] = { isCapture: targetSet.has(dst) };
      });
      return out;
    }
    try {
      const c = new Chess(boardFen);
      const mv = c.moves({ square: sq, verbose: true });
      const out = {};
      mv.forEach(m => { out[m.to] = { isCapture: !!(m.flags && (m.flags.includes('c') || m.flags.includes('e'))) }; });
      return out;
    } catch { return {}; }
  };

  const onBoardSquareClick = (sq) => {
    if (!isTeacher || (!analysisMode && !treeViewActive && ply !== null) || !boardFen) return;
    // Capture Activity: use the overlay as the source of truth. Only the
    // student's piece may be selected; legal dests come from the same
    // ray-based helpers used for drag validation, so click-to-move and
    // drag & drop stay 1:1. onPieceDrop's Capture Overlay branch (line 423)
    // is reused for the actual move execution — no scoring / timer / drag
    // / monitor / player logic is touched.
    if (room?.capture_overlay) {
      const cfg = room.capture_overlay;
      const student = (Array.isArray(cfg.pieces) ? cfg.pieces : []).find(p => p.role === 'student');
      if (!student) return;
      if (selectedSquare) {
        if (sq === selectedSquare) { setSelectedSquare(null); setLegalDests({}); return; }
        if (legalDests[sq]) {
          const from = selectedSquare;
          setSelectedSquare(null); setLegalDests({}); // clear hints BEFORE executing so the post-move render is clean
          onPieceDrop(from, sq);
          return;
        }
        // Re-selecting the student's own piece keeps hints; anything else clears.
        if (sq === student.square) {
          const dests = computeLegalDests(sq);
          setSelectedSquare(sq); setLegalDests(dests); return;
        }
        setSelectedSquare(null); setLegalDests({});
        return;
      }
      if (sq !== student.square) return;
      const dests = computeLegalDests(sq);
      if (Object.keys(dests).length === 0) return;
      setSelectedSquare(sq); setLegalDests(dests);
      return;
    }
    if (selectedSquare) {
      if (sq === selectedSquare) { setSelectedSquare(null); setLegalDests({}); return; }
      if (legalDests[sq]) {
        const from = selectedSquare;
        setSelectedSquare(null); setLegalDests({}); // clear hints BEFORE executing so the post-move render is clean
        onPieceDrop(from, sq);
        return;
      }
      try {
        const c = new Chess(boardFen);
        const piece = c.get(sq);
        if (piece && piece.color === c.turn()) {
          setSelectedSquare(sq); setLegalDests(computeLegalDests(sq)); return;
        }
      } catch { /* noop */ }
      setSelectedSquare(null); setLegalDests({});
      return;
    }
    try {
      const c = new Chess(boardFen);
      const piece = c.get(sq);
      if (!piece || piece.color !== c.turn()) {
        // Empty square (or opponent piece) with NO selection — chess.com-style
        // left-click on empty area clears all annotations. Only runs when the
        // click could not have started a valid move: no piece of the current
        // turn on the target square. Click-to-move and drag & drop are never
        // intercepted because they end up in earlier branches / handlePieceDrop.
        if ((room?.arrows?.length || 0) > 0 || Object.keys(room?.circles || {}).length > 0) {
          onAnnotationsChange([], {});
        }
        return;
      }
    } catch { return; }
    const dests = computeLegalDests(sq);
    if (Object.keys(dests).length === 0) return;
    setSelectedSquare(sq); setLegalDests(dests);
  };

  const movesForDisplay = (isTeacher || room.moves_visible) ? (room.moves || []) : [];
  const canSeeMoves = isTeacher || room.moves_visible;
  // Keep the shared moves length ref fresh for the ← / → keyboard callbacks.
  movesLenRef.current = movesForDisplay.length;

  // ============================================================
  // Classroom mode: explanation | exercise
  // - explanation: teacher's board is the main view for everyone
  // - exercise: teacher launched a puzzle; STUDENTS see only the exercise
  //   while the teacher keeps full control (his explanation board stays).
  // The teacher's FEN is preserved on the server across modes.
  // ============================================================
  const classroomMode = room.active_puzzle ? 'exercise' : 'explanation';
  const studentExerciseMode = !isTeacher && classroomMode === 'exercise';
  // Teacher's main-area view during a Capture Activity is a WIDE Live Monitor
  // (TeacherCaptureLiveView). The explanation board itself is preserved
  // server-side (room.fen/moves/arrows/circles never change during ASK) so
  // when the teacher pulses "Cerrar ejercicio" and active_puzzle becomes
  // None, this flips back to false and the main area falls back to the
  // explanation <ChessBoardView> unchanged.
  const teacherCaptureMonitorMode = isTeacher && classroomMode === 'exercise' && !!room.active_puzzle?.capture_config;
  // Symmetric flag for NORMAL puzzles (non-Capture) — mutually exclusive with
  // teacherCaptureMonitorMode by construction (!capture_config). Renders
  // TeacherPuzzleLiveView in the main area so the teacher can monitor every
  // student's live progress on tactical exercises pushed via ASK. Data comes
  // exclusively from the SAME polling channel Capture already uses
  // (room.puzzle_states populated by /puzzle/attempt).
  const teacherPuzzleMonitorMode = isTeacher && classroomMode === 'exercise' && !!room.active_puzzle && !room.active_puzzle.capture_config;

  // ============================================================
  // C1 · Recepción de Explanation por el ALUMNO
  // ============================================================
  // Cuando el profesor inicia una Explanation (C0 escribe `active_explanation`
  // en la sala), el alumno la recibe automáticamente vía el polling existente
  // y muestra el Step oficial (`current_step`, fuente de verdad = servidor).
  // Aditivo: si no hay `active_explanation`, todo el comportamiento actual
  // (explanation board / active_puzzle / capture) queda intacto. La navegación
  // del profesor (avanzar/retroceder/finalizar) llegará en C2.
  const studentExplanationMode = !isTeacher && !!room.active_explanation;
  // C-fix · Mientras una Explanation dirigida esté activa, el overlay del
  // ExplanationViewer es el único tablero que debe existir. Desmontamos el
  // tablero principal del Classroom (su ChessBoardView + DndContext) para
  // eliminar la interferencia de eventos click que impedía Challenge Square.
  // Al finalizar (active_explanation === null) se vuelve a montar y su estado
  // proviene del servidor (room.fen). Afecta a ambos roles porque ambos ven el
  // overlay durante la clase dirigida.
  const explanationActive = !!room.active_explanation;

  // UX-4 · Estado compacto para el header del alumno (nombre · estado · salir).
  const studentStatus = room.active_explanation
    ? { label: 'Explicación', cls: 'bg-indigo-100 text-indigo-700' }
    : room.active_puzzle
      ? { label: 'Ejercicio', cls: 'bg-amber-100 text-amber-700' }
      : { label: 'En vivo', cls: 'bg-emerald-100 text-emerald-700' };

  return (
    <div className="h-screen bg-[#F1F5F9] flex flex-col overflow-hidden">
      {/* Header — single row, responsive */}
      <header className="bg-white border-b border-slate-200 px-4 py-2 flex flex-wrap items-center justify-between gap-y-1 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="font-display text-lg sm:text-xl font-extrabold truncate" data-testid="classroom-name">{room.name}</div>
          {!isTeacher ? (
            <span data-testid="classroom-status-badge" className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold whitespace-nowrap ${studentStatus.cls}`}>
              {studentStatus.label}
            </span>
          ) : (
            <button onClick={copyCode} className="inline-flex items-center gap-1 rounded-full bg-slate-100 hover:bg-slate-200 px-3 py-1 text-xs font-mono tracking-widest">
              {room.code} <Copy className="w-3 h-3"/>
            </button>
          )}
          {room.video_url && (
            <a href={room.video_url} target="_blank" rel="noreferrer"
              data-testid="join-video-btn"
              className="inline-flex items-center gap-1 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-800 px-3 py-1 text-xs font-bold">
              <Video className="w-3 h-3"/> <span className="hidden sm:inline">Entrar al video</span> <ExternalLink className="w-3 h-3"/>
            </a>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isTeacher && (room.raised_hands || []).length > 0 && (
            <div className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-xs font-bold">
              <Hand className="w-3.5 h-3.5"/> {(room.raised_hands||[]).length} mano{(room.raised_hands||[]).length>1?'s':''} levantada{(room.raised_hands||[]).length>1?'s':''}
            </div>
          )}
          {isTeacher && (
            <Button data-testid="video-toggle-btn" onClick={toggleVideo} variant={room.video_url ? 'destructive' : 'default'}
              className="rounded-full">
              {room.video_url ? <><VideoOff className="w-4 h-4 mr-1"/> Terminar video</> : <><Video className="w-4 h-4 mr-1"/> Start video call</>}
            </Button>
          )}
          {isTeacher && (
            <Button data-testid="header-chat-btn" onClick={()=>setChatPopoverOpen(o=>!o)}
              variant={chatPopoverOpen ? 'default' : 'outline'} size="icon" className="relative rounded-full" title="Chat">
              <MessageCircle className="w-4 h-4"/>
              {unreadChat > 0 && <span data-testid="header-chat-unread-badge" className="absolute -top-1.5 -right-1.5 inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white shadow">{unreadChat > 9 ? '9+' : unreadChat}</span>}
            </Button>
          )}
          {isTeacher && (
            <Button data-testid="aside-toggle-btn" onClick={()=>setAsideOpen(o=>!o)} variant="outline" size="sm" className="rounded-full">
              <Layers className="w-4 h-4 mr-1"/> {asideOpen ? 'Ocultar panel' : 'Mostrar panel'}
            </Button>
          )}
          {classEnded && (
            <span data-testid="class-ended-badge" className="inline-flex items-center gap-1 rounded-full bg-slate-200 text-slate-700 px-3 py-1 text-xs font-bold whitespace-nowrap">
              <Flag className="w-3.5 h-3.5"/> Clase terminada
            </span>
          )}
          {canManage && room.active === true && (
            <Button data-testid="end-classroom-btn" onClick={()=>setEndDialogOpen(true)} variant="outline"
              className="rounded-md border-rose-300 text-rose-600 hover:bg-rose-50 font-bold px-4">
              <Flag className="w-4 h-4 mr-2"/> Terminar clase
            </Button>
          )}
          <Button data-testid="exit-classroom-btn" onClick={exitRoom} className="rounded-md bg-rose-500 hover:bg-rose-600 text-white font-bold px-4">
            <LogOut className="w-4 h-4 mr-2"/> Salir
          </Button>
        </div>
      </header>

      {/* E2 · Confirmación de "Terminar clase" */}
      <Dialog open={endDialogOpen} onOpenChange={setEndDialogOpen}>
        <DialogContent data-testid="end-class-dialog">
          <DialogHeader>
            <DialogTitle>¿Terminar esta clase?</DialogTitle>
            <DialogDescription>
              Esta acción finalizará la clase para todos los participantes. Los alumnos ya no podrán volver a entrar como clase activa.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" data-testid="end-class-cancel" onClick={()=>setEndDialogOpen(false)} disabled={endingClass}>
              Cancelar
            </Button>
            <Button data-testid="end-class-confirm" onClick={endClass} disabled={endingClass}
              className="bg-rose-600 hover:bg-rose-700 text-white">
              {endingClass ? 'Terminando…' : 'Terminar clase'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex-1 min-h-0 flex flex-col gap-4 p-4">
        {/*
          Main layout — 2 columns (Board | Sidebar) with an optional collapsible Material
          drawer as a full-width bottom row. This replaces the previous cramped 3-column
          layout so the board and the tabs panel never fight for space.
        */}
        <div className={`flex-1 min-h-0 grid grid-cols-1 gap-4 ${(isTeacher && asideOpen) ? 'lg:grid-cols-[minmax(0,1fr)_380px]' : ''}`}>
        {/* Board area — Explanation board / Teacher monitor / Student exercise */}
        <div data-testid={studentExerciseMode ? 'student-exercise-view' : (teacherCaptureMonitorMode ? 'teacher-capture-monitor-view' : (teacherPuzzleMonitorMode ? 'teacher-puzzle-monitor-view' : 'explanation-board'))} className={`bg-white rounded-2xl border border-slate-200 p-4 flex flex-col items-center min-h-0 overflow-hidden relative ${studentExerciseMode ? "pt-1" : ""}`} style={studentExerciseMode ? { overflowAnchor: 'none' } : undefined}>
          {!isTeacher && (
            <div
              className={`${isMobile
                ? 'relative w-full shrink-0 flex items-center justify-end gap-2 mb-2'
                : 'absolute top-4 right-4 z-10 flex items-center justify-end gap-2'}`}
              data-testid="student-board-actions"
            >
              <Button data-testid="raise-hand-btn" onClick={raiseHand} variant="outline" size="sm" className="rounded-full">
                <Hand className="w-4 h-4 mr-1 text-amber-500"/> Levantar mano
              </Button>
              <Button data-testid="open-tools-btn" onClick={()=>setPanelOpen(true)} variant="outline" size="sm" className="relative rounded-full">
                <Layers className="w-4 h-4 mr-1"/> Herramientas
                {unreadChat > 0 && <span data-testid="tools-chat-unread-badge" className="absolute -top-1.5 -right-1.5 inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white shadow">{unreadChat > 9 ? '9+' : unreadChat}</span>}
              </Button>
            </div>
          )}
          {studentExerciseMode ? (
            <div ref={boardWrapRef} className="w-full h-full min-h-0 flex justify-center overflow-hidden">
              <LivePuzzle room={room} isTeacher={false} currentUserId={user?.id} expanded boardWidth={!isMobile && !room.active_puzzle?.capture_config ? Math.min(boardSize, 520) : boardSize} fitHeight={!isMobile && !room.active_puzzle?.capture_config} embedded/>
            </div>
          ) : teacherCaptureMonitorMode ? (
            <div className="w-full h-full min-h-0 flex flex-col overflow-hidden">
              <TeacherCaptureLiveView room={room} currentUserId={user?.id}/>
            </div>
          ) : teacherPuzzleMonitorMode ? (
            <div className="w-full h-full min-h-0 flex flex-col overflow-hidden">
              <TeacherPuzzleLiveView room={room}/>
            </div>
          ) : explanationActive ? (
            <div className="w-full max-w-[900px] grid place-items-center text-slate-400 text-sm py-16" data-testid="explanation-active-placeholder">
              Clase de explicación en curso…
            </div>
          ) : (
          <div ref={boardWrapRef} className="w-full max-w-[900px] flex justify-center">
            <ChessBoardView
              key={treeViewActive ? `an-${navEpoch}` : 'live'}
              fen={boardFen}
              onPieceDrop={onPieceDrop}
              onSquareClick={isTeacher && (treeViewActive || (!treeViewActive && ply === null)) ? onBoardSquareClick : undefined}
              orientation={orientation}
              boardWidth={boardSize}
              arePiecesDraggable={isTeacher && (treeViewActive || (!treeViewActive && ply === null))}
              allowAnnotations={isTeacher && ply === null}
              arrows={room.arrows || EMPTY_ARR}
              circles={room.circles || EMPTY_OBJ}
              extraSquareStyles={boardSquareStyles}
              onAnnotationsChange={onAnnotationsChange}
              disableAnimation={!analysisMode && (skipBoardAnim || (EXP_CAPTURE_DISABLE_ANIM && !!room?.capture_overlay) || (EXP_DB_DISABLE_ANIM && !room?.capture_overlay && !room?.active_puzzle && !!room?.starting_fen && room.starting_fen !== DEFAULT_STARTING_FEN))}
              onDragActiveChange={(active) => { dragActiveRef.current = active; }}
            />
          </div>
          )}
          {/* Promotion picker */}
          <Dialog open={!!pendingPromotion} onOpenChange={(v)=>{ if (!v) setPendingPromotion(null); }}>
            <DialogContent className="max-w-sm" data-testid="promotion-dialog">
              <DialogHeader><DialogTitle>Elige la pieza de coronación</DialogTitle></DialogHeader>
              <div className="grid grid-cols-4 gap-3 py-2">
                {[
                  { k: 'q', label: 'Dama',    glyph: pendingPromotion?.color === 'w' ? '♕' : '♛' },
                  { k: 'r', label: 'Torre',   glyph: pendingPromotion?.color === 'w' ? '♖' : '♜' },
                  { k: 'b', label: 'Alfil',   glyph: pendingPromotion?.color === 'w' ? '♗' : '♝' },
                  { k: 'n', label: 'Caballo', glyph: pendingPromotion?.color === 'w' ? '♘' : '♞' },
                ].map(p => (
                  <button key={p.k}
                    data-testid={`promote-${p.k}`}
                    onClick={()=>applyPromotion(p.k)}
                    className="flex flex-col items-center gap-1 rounded-xl border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 p-3 transition-colors">
                    <div className="text-5xl leading-none">{p.glyph}</div>
                    <div className="text-xs font-semibold text-slate-700">{p.label}</div>
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500 text-center">Escoge la pieza en la que se convierte el peón.</p>
            </DialogContent>
          </Dialog>
          {/* Board toolbar — hidden for students during an exercise AND for teacher during a Capture Activity monitor (main area swapped) */}
          {!studentExerciseMode && isTeacher && !teacherCaptureMonitorMode && !teacherPuzzleMonitorMode && (
          <div className="mt-4 w-full max-w-[900px] flex flex-wrap items-center justify-center gap-1 bg-slate-50 rounded-2xl px-3 py-2">
            {/* Group 1 — Navegación: oculto para alumno durante explicación dirigida */}
            {!(!isTeacher && !!room.active_explanation) && (
              <>
                <div className="flex items-center gap-0.5">
                  <Button data-testid="board-first" size="icon" variant="ghost" onClick={treeViewActive ? analysisFirst : goFirst} disabled={!treeViewActive && !canSeeMoves}><SkipBack className="w-4 h-4"/></Button>
                  <Button data-testid="board-prev" size="icon" variant="ghost" onClick={treeViewActive ? analysisBack : goPrev} disabled={!treeViewActive && !canSeeMoves}><ChevronLeft className="w-4 h-4"/></Button>
                  <Button data-testid="board-next" size="icon" variant="ghost" onClick={treeViewActive ? analysisNext : goNext} disabled={!treeViewActive && !canSeeMoves}><ChevronRight className="w-4 h-4"/></Button>
                  <Button data-testid="board-live" size="icon" variant="ghost" onClick={treeViewActive ? analysisLast : goLive}><SkipForward className="w-4 h-4"/></Button>
                </div>
                <div className="w-px h-6 bg-slate-200 mx-1"/>
              </>
            )}
            {/* Group 2 — Vista */}
            <div className="flex items-center gap-0.5">
              <Button
                data-testid="board-flip"
                size="icon"
                variant="ghost"
                onClick={() => {
                  const nextOrientation = orientation === 'white' ? 'black' : 'white';
                  setOrientation(nextOrientation);
                  if (isTeacher) {
                    api.post(`/classrooms/${id}/orientation`, {
                      orientation: nextOrientation
                    }).catch(() => {});
                  }
                }}
                title="Voltear tablero"
              >
                <FlipHorizontal2 className="w-4 h-4"/>
              </Button>
              {isTeacher && <Button data-testid="board-reset" size="icon" variant="ghost" onClick={reset} title="Reset — comenzar tablero nuevo desde la posición estándar"><RotateCcw className="w-4 h-4"/></Button>}
              {isTeacher && ((room.arrows||[]).length > 0 || Object.keys(room.circles||{}).length > 0) && (
                <Button data-testid="board-clear-annotations" size="sm" variant="ghost" onClick={clearAnnotations} title="Borrar todas las flechas y casillas resaltadas">
                  <Eraser className="w-4 h-4 mr-1"/> Limpiar
                </Button>
              )}
            </div>
            {isTeacher && <div className="w-px h-6 bg-slate-200 mx-1"/>}
            {/* Group 3 — Posición */}
            {isTeacher && (
              <div className="flex items-center gap-0.5">
              <Dialog open={openLoad} onOpenChange={setOpenLoad}>
                <DialogTrigger asChild>
                  <Button data-testid="board-load" size="sm" variant="ghost"><Upload className="w-4 h-4 mr-1"/> Cargar</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle>Cargar posición o partida</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <Input placeholder="FEN (opcional)" className="font-mono text-xs" value={fenInput} onChange={(e)=>setFenInput(e.target.value)}/>
                    <Textarea placeholder="PGN (opcional)" className="font-mono text-xs" rows={8} value={pgnInput} onChange={(e)=>setPgnInput(e.target.value)}/>
                    <div className="flex items-center gap-2">
                      <input type="file" accept=".pgn,text/plain" data-testid="board-pgn-file"
                        onChange={async (e)=>{
                          const f = e.target.files?.[0]; if(!f) return;
                          const txt = await f.text();
                          setPgnInput(txt);
                          toast.success(`PGN cargado: ${f.name}`);
                        }}
                        className="text-xs"/>
                    </div>
                  </div>
                  <DialogFooter><Button data-testid="board-load-save" onClick={loadContent}>Cargar</Button></DialogFooter>
                </DialogContent>
              </Dialog>
              <Dialog open={openEdit} onOpenChange={(v)=>{
                setOpenEdit(v);
                if (v) {
                  const initialFen = room.fen || STARTING_FEN;
                  const parts = initialFen.split(' ');
                  setEditFen(initialFen);
                  setEditCastling(parts[2] || '-');
                  setEditEnPassant(parts[3] || '-');
                  setEditTool(null);
                }
              }}>
                <DialogTrigger asChild>
                  <Button data-testid="board-edit" size="sm" variant="ghost">
                    <Pencil className="w-4 h-4 mr-1"/> Editar
                  </Button>
                </DialogTrigger>

                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto top-[5vh] translate-y-0" data-testid="edit-position-dialog">
                  <DialogHeader>
                    <DialogTitle>Editar posición del tablero</DialogTitle>
                    <DialogDescription>
                      Coloca, mueve o elimina piezas para crear la posición que quieres mostrar a tus alumnos.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="grid grid-cols-1 md:grid-cols-[360px_1fr] gap-6 items-start">
                    <div className="flex justify-center">
                      <ChessBoardView
                        fen={editFen || STARTING_FEN}
                        boardWidth={360}
                        arePiecesDraggable={true}
                        onPieceDrop={(from, to) => {
                          try {
                            const board = new Chess(editFen || STARTING_FEN, { skipValidation: true });
                            const piece = board.get(from);
                            if (!piece) return false;

                            board.remove(from);

                            if (editTool === 'erase') {
                              board.remove(to);
                            } else {
                              board.remove(to);
                              board.put(piece, to);
                            }

                            setEditFen(board.fen());
                            return true;
                          } catch {
                            return false;
                          }
                        }}
                        onSquareClick={(square) => {
                          if (!editTool) return;

                          if (editTool === 'erase') {
                            updateEditSquare(square, null);
                            return;
                          }

                          updateEditSquare(square, editTool);
                        }}
                      />
                    </div>

                    <div className="grid grid-cols-[minmax(0,1fr)_180px] gap-4 items-start">
                      <div className="space-y-3">
                        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">
                          Piezas
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                          {[
                            ['wK', '♔'], ['wQ', '♕'], ['wR', '♖'],
                            ['wB', '♗'], ['wN', '♘'], ['wP', '♙'],
                            ['bK', '♚'], ['bQ', '♛'], ['bR', '♜'],
                            ['bB', '♝'], ['bN', '♞'], ['bP', '♟'],
                          ].map(([piece, symbol]) => (
                            <Button
                              key={piece}
                              type="button"
                              size="sm"
                              variant={editTool === piece ? "default" : "outline"}
                              className="h-11 text-2xl"
                              onClick={() => setEditTool(piece)}
                              title={`Colocar ${piece}`}
                            >
                              {symbol}
                            </Button>
                          ))}
                        </div>

                        <div className="border-t pt-3">
                          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">
                            Enroque
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant={editCastling.includes('K') ? "default" : "outline"}
                              onClick={() => {
                                const value = editCastling.includes('K')
                                  ? editCastling.replace('K', '') || '-'
                                  : (editCastling === '-' ? 'K' : editCastling + 'K');
                                updateEditSpecialFields(value, editEnPassant);
                              }}
                            >
                              Blancas O-O
                            </Button>

                            <Button
                              type="button"
                              size="sm"
                              variant={editCastling.includes('Q') ? "default" : "outline"}
                              onClick={() => {
                                const value = editCastling.includes('Q')
                                  ? editCastling.replace('Q', '') || '-'
                                  : (editCastling === '-' ? 'Q' : editCastling + 'Q');
                                updateEditSpecialFields(value, editEnPassant);
                              }}
                            >
                              Blancas O-O-O
                            </Button>

                            <Button
                              type="button"
                              size="sm"
                              variant={editCastling.includes('k') ? "default" : "outline"}
                              onClick={() => {
                                const value = editCastling.includes('k')
                                  ? editCastling.replace('k', '') || '-'
                                  : (editCastling === '-' ? 'k' : editCastling + 'k');
                                updateEditSpecialFields(value, editEnPassant);
                              }}
                            >
                              Negras O-O
                            </Button>

                            <Button
                              type="button"
                              size="sm"
                              variant={editCastling.includes('q') ? "default" : "outline"}
                              onClick={() => {
                                const value = editCastling.includes('q')
                                  ? editCastling.replace('q', '') || '-'
                                  : (editCastling === '-' ? 'q' : editCastling + 'q');
                                updateEditSpecialFields(value, editEnPassant);
                              }}
                            >
                              Negras O-O-O
                            </Button>
                          </div>
                        </div>

                        <div className="border-t pt-3">
                          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">
                            Al paso
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant={editEnPassant === '-' ? "default" : "outline"}
                              onClick={() => updateEditSpecialFields(editCastling, '-')}
                            >
                              Sin al paso
                            </Button>

                            <select
                              className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                              value={editEnPassant === '-' ? '' : editEnPassant}
                              onChange={(e) => updateEditSpecialFields(
                                editCastling,
                                e.target.value || '-'
                              )}
                            >
                              <option value="">Casilla</option>
                              {['a3','b3','c3','d3','e3','f3','g3','h3','a6','b6','c6','d6','e6','f6','g6','h6'].map((square) => (
                                <option key={square} value={square}>{square}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                      </div>

                      <div className="space-y-3 pt-7">
                        <Button
                          type="button"
                          size="sm"
                          variant={editTool === 'erase' ? "default" : "outline"}
                          className="w-full"
                          onClick={() => setEditTool('erase')}
                        >
                          <Eraser className="w-4 h-4 mr-2"/>
                          Borrar pieza
                        </Button>

                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            setEditFen(STARTING_FEN);
                            setEditTool(null);
                          }}
                        >
                          Posición inicial
                        </Button>

                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            setEditFen(EMPTY_FEN);
                            setEditTool(null);
                          }}
                        >
                          Tablero vacío
                        </Button>

                        <div className="border-t pt-3">
                          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">
                            Turno
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant={editFen.split(' ')[1] === 'w' ? "default" : "outline"}
                              onClick={() => {
                                const parts = editFen.split(' ');
                                parts[1] = 'w';
                                setEditFen(parts.join(' '));
                              }}
                            >
                              Blancas
                            </Button>

                            <Button
                              type="button"
                              size="sm"
                              variant={editFen.split(' ')[1] === 'b' ? "default" : "outline"}
                              onClick={() => {
                                const parts = editFen.split(' ');
                                parts[1] = 'b';
                                setEditFen(parts.join(' '));
                              }}
                            >
                              Negras
                            </Button>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-2 pt-3">
                          <Button
                            data-testid="edit-fen-save"
                            onClick={async ()=>{
                              await pushFen(editFen.trim(), 'Posición actualizada');
                              setOpenEdit(false);
                            }}
                          >
                            Aplicar posición
                          </Button>

                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setOpenEdit(false)}
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-3">
                    <details>
                      <summary className="cursor-pointer text-sm font-medium text-slate-600">
                        FEN avanzado
                      </summary>
                      <Textarea
                        data-testid="edit-fen-input"
                        rows={2}
                        className="font-mono text-xs mt-2"
                        value={editFen}
                        onChange={(e)=>setEditFen(e.target.value)}
                      />
                    </details>
                  </div>


                </DialogContent>
              </Dialog>
              </div>
            )}
            {isTeacher && <div className="w-px h-6 bg-slate-200 mx-1"/>}
            {/* Group 4 — Modos de juego */}
            {isTeacher && (
              <div className="flex items-center gap-0.5 flex-wrap">
              <Button data-testid="board-illegal-toggle"
                size="sm"
                variant={illegalMode ? 'default' : 'ghost'}
                className={illegalMode ? 'bg-rose-500 hover:bg-rose-600 text-white' : ''}
                onClick={()=>setIllegalMode(v => !v)}
                title="Permite mover cualquier pieza a cualquier casilla varias veces seguidas">
                <AlertTriangle className="w-4 h-4 mr-1"/> Ilegales {illegalMode ? 'ON' : 'OFF'}
              </Button>
              </div>
            )}
            <div className="ml-auto pl-2 text-xs font-mono text-slate-500">
              {ply === null ? 'en vivo' : `jugada ${ply}/${movesForDisplay.length}`}
            </div>
          </div>
          )}
          {isTeacher && !studentExerciseMode && !teacherCaptureMonitorMode && !teacherPuzzleMonitorMode && (
            <div className="mt-3 w-full max-w-[900px] flex items-center gap-3 text-xs text-slate-600">
              <Switch data-testid="moves-visible-toggle" checked={!!room.moves_visible} onCheckedChange={toggleMovesVisible}/>
              <span>Los movimientos son visibles para los participantes</span>
            </div>
          )}
        </div>

        {/* Right panel — profesor: columna inline (UX-2) · alumno (UX-4):
            overlay Sheet lateral (≥md) / Drawer inferior (móvil), inicia
            cerrado para que el tablero sea dominante. Mismo contenido y
            funcionalidad para ambos roles — solo cambia la presentación. */}
        {!isTeacher && panelOpen && (
          <div onClick={()=>setPanelOpen(false)} data-testid="student-panel-backdrop"
            className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200" />
        )}
        {((isTeacher && asideOpen) || (!isTeacher && panelOpen)) && (
        <aside data-testid={isTeacher ? undefined : 'student-tools-panel'}
          className={isTeacher
            ? "bg-white rounded-2xl border border-slate-200 flex flex-col h-full min-h-0 overflow-hidden"
            : `bg-white flex flex-col overflow-hidden shadow-2xl ${isMobile
                ? 'fixed inset-x-0 bottom-0 z-50 h-[80vh] rounded-t-2xl border-t border-slate-200 animate-in slide-in-from-bottom duration-300'
                : 'fixed top-0 right-0 z-50 h-full w-[380px] max-w-[92vw] border-l border-slate-200 animate-in slide-in-from-right duration-300'}`}>
          {!isTeacher && (
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0">
              <div className="font-display font-extrabold text-sm uppercase tracking-widest text-slate-600 flex items-center gap-2">
                <Layers className="w-4 h-4 text-blue-500"/> Herramientas
              </div>
              <Button data-testid="close-tools-btn" size="icon" variant="ghost" className="rounded-full" onClick={()=>setPanelOpen(false)}>
                <X className="w-4 h-4"/>
              </Button>
            </div>
          )}
          {/* ─────────── TOP: Ejercicios (or Material when toggled) ─────────── */}
          <section data-testid={showMaterial ? 'material-section' : 'exercises-section'} className={`${(takingAttendance && activeBottomTab === 'participants') ? 'hidden' : `flex flex-col min-h-0 ${!showMaterial ? 'border-b border-slate-200' : ''}`}`} style={{ flex: showMaterial ? '1 1 auto' : (isTeacher ? '0 0 30%' : '0 0 auto') }}>
            <div className="flex items-center justify-between px-4 pt-3 pb-2 shrink-0">
              <div className="font-display font-extrabold text-sm uppercase tracking-widest text-slate-600 flex items-center gap-2">
                {showMaterial
                  ? (<><Layers className="w-4 h-4 text-blue-500"/> Material</>)
                  : (<><PuzzleIcon className="w-4 h-4 text-amber-500"/> Ejercicios</>)}
              </div>
              {isTeacher && (
                <div className="flex items-center gap-2">
                  {showMaterial ? (
                    <Button data-testid="close-material-btn" size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={()=>setShowMaterial(false)}>
                      <ChevronLeft className="w-3 h-3 mr-1"/> Volver a ejercicios
                    </Button>
                  ) : (
                    <>
                      <Button data-testid="toggle-material-btn" size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={()=>setShowMaterial(true)}>
                        <Layers className="w-3 h-3 mr-1"/> Material
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
            {showMaterial && isTeacher ? (
              <div className="flex-1 min-h-0 overflow-hidden" data-testid="material-embed">
                <ClassroomMaterial
                  roomId={id}
                  isTeacher={isTeacher}
                  onLoadGame={loadGameToBoard}
                  onLaunch={handleLaunchExercise}
                  onHide={handleHideMaterial}
                  onStartExplanation={startExplanation}
                />
              </div>
            ) : (
            <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-3 space-y-3">
              {!studentExerciseMode && room.active_puzzle && (
                <LivePuzzle room={room} isTeacher={isTeacher} currentUserId={user?.id}/>
              )}
              {!isTeacher && !room.active_puzzle && (
                <div className="text-xs text-slate-500 text-center py-6">
                  Sin ejercicio activo. El profesor puede lanzar uno en cualquier momento.
                </div>
              )}
            </div>
            )}
          </section>

          {/* ─────────── BOTTOM: Tabs (hidden while Material is open) ─────────── */}
          <Tabs value={activeBottomTab} onValueChange={setActiveBottomTab} className={`relative flex-1 min-h-0 min-w-0 flex flex-col ${showMaterial && !takingAttendance ? 'hidden' : ''}`}>
            <TabsList className={`grid ${isTeacher ? 'grid-cols-3' : 'grid-cols-4'} w-auto rounded-none border-b bg-white p-1 mx-2 mt-2 shrink-0`}>
              <TabsTrigger data-testid="tab-moves" value="moves" className="text-[11px] px-1 gap-1 min-w-0"><ListOrdered className="w-4 h-4 shrink-0"/> <span className="truncate">Jugadas</span></TabsTrigger>
              {!isTeacher && (
              <TabsTrigger data-testid="tab-chat" value="chat" className="relative text-[11px] px-1 gap-1 min-w-0"><MessageCircle className="w-4 h-4 shrink-0"/> <span className="truncate">Chat</span>{unreadChat > 0 && <span data-testid="chat-unread-badge" className="ml-0.5 inline-flex min-w-[16px] h-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">{unreadChat > 9 ? '9+' : unreadChat}</span>}</TabsTrigger>
              )}
              <TabsTrigger data-testid="tab-leaderboard" value="leaderboard" className="text-[11px] px-1 gap-1 min-w-0"><Trophy className="w-4 h-4 shrink-0"/> <span className="truncate">Ranking</span></TabsTrigger>
              <TabsTrigger data-testid="tab-participants" value="participants" className="text-[11px] px-1 gap-1 min-w-0"><Users className="w-4 h-4 shrink-0"/> <span className="truncate">Personas</span></TabsTrigger>
            </TabsList>

            <TabsContent value="moves" className="flex-[1_1_0%] min-h-0 min-w-0 p-4 data-[state=active]:flex data-[state=inactive]:hidden flex-col overflow-hidden">
              <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden space-y-3">
              {isTeacher && (room.solution_moves || []).length > 0 && (
                <div data-testid="preloaded-solution" className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-2">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-indigo-700 mb-1">Solución precargada</div>
                  <div className="font-mono text-xs text-indigo-900 leading-relaxed break-words">
                    {(room.solution_moves || []).map((san, i) => (
                      <span key={i} className="inline-block mr-1.5">
                        {i%2===0 && <span className="text-indigo-400">{Math.floor(i/2)+1}.</span>} {san}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {treeViewActive && moveTree ? (
                // Panel de jugadas del árbol de análisis.
                // La estructura del moveTree no cambia aquí: este bloque
                // solamente controla cómo se representa visualmente.
                (() => {
                  const root = moveTree.nodes[moveTree.rootId];
                  const currentId = moveTree.currentId;

                  const isCurrent = (nodeId) => nodeId === currentId;

                  const getNodePly = (node) => {
                    if (!node?.id || !moveTree) return 0;

                    const path = getPath(moveTree, node.id);

                    // El ply depende de la profundidad real del nodo
                    // dentro del árbol, nunca del FEN.
                    return path.filter((pathNode) => pathNode?.move).length;
                  };

                  const moveLabel = (node) => {
                    if (!node?.move) return '';

                    const ply = getNodePly(node);
                    const moveNo = Math.ceil(ply / 2);

                    return ply % 2 === 1
                      ? `${moveNo}. ${node.move.san}`
                      : `${moveNo}... ${node.move.san}`;
                  };

                  const renderMoveButton = (node, isVariation = false, isVariationStart = false) => (
                    <button
                      key={node.id}
                      type="button"
                      data-testid={`analysis-tree-move-${node.id}`}
                      onClick={() => analysisGoto(node.id)}
                      className={`inline-flex items-center cursor-pointer px-1.5 py-0.5 rounded transition-colors text-left ${
                        isCurrent(node.id)
                          ? 'bg-amber-300 font-bold text-slate-900'
                          : isVariation
                            ? 'text-slate-500 hover:bg-slate-100'
                            : 'text-slate-800 hover:bg-slate-100'
                      }`}
                    >
                      {isVariationStart && (
                        <span
                          aria-hidden="true"
                          className="mr-1 text-slate-400 font-normal"
                        >
                          ↳
                        </span>
                      )}
                      {moveLabel(node)}
                    </button>
                  );

                  /*
                   * Renderiza una rama secundaria completa.
                   * children[0] continúa esa rama.
                   * children[1+] crean nuevas ramas anidadas.
                   */
                  /*
                   * Renderiza una rama completa del árbol.
                   *
                   * Esta función representa SOLO una rama que nace de un
                   * padre concreto. Su primer nodo es el movimiento de esa
                   * rama y children[0] continúa esa misma rama.
                   *
                   * Las demás children[1+] vuelven a ser ramas hermanas
                   * del nodo actual.
                   */
                  /*
                   * Renderer recursivo del árbol de análisis.
                   *
                   * Regla visual:
                   *
                   *   nodo
                   *   └── hijos
                   *       ├── hijo principal
                   *       │   └── sus hijos
                   *       └── variante hermana
                   *
                   * Cada hijo pertenece directamente al mismo padre.
                   * Esto evita que una variante profunda parezca ser
                   * continuación de otra variante hermana.
                   */
                  const renderVariationBranch = (
                    nodeId,
                    branchKey,
                    depth = 0,
                    isVariation = false,
                    isVariationStart = false
                  ) => {
                    const node = moveTree.nodes[nodeId];
                    if (!node?.move) return null;

                    const children = (node.childrenIds || [])
                      .map((childId) => moveTree.nodes[childId])
                      .filter(Boolean);

                    return (
                      <div
                        key={`${branchKey}-node-${node.id}`}
                        className="space-y-0.5"
                      >
                        {renderMoveButton(node, isVariation, isVariationStart)}

                        {children.length > 0 && (
                          <div
                            data-testid={`analysis-tree-children-${node.id}`}
                            className="ml-3 pl-2 border-l-2 border-slate-200 space-y-0.5"
                          >
                            {children.map((child, index) => (
                              <div
                                key={`${branchKey}-child-${child.id}`}
                                data-testid={`analysis-tree-child-${child.id}`}
                                className="relative"
                              >
                                <span
                                  aria-hidden="true"
                                  className="absolute -left-2 top-3 w-2 border-t border-slate-200"
                                />

                                <div
                                  className={
                                    index === 0
                                      ? "space-y-0.5"
                                      : "space-y-0.5"
                                  }
                                >
                                  {renderVariationBranch(
                                    child.id,
                                    `${branchKey}-${child.id}`,
                                    depth + 1,
                                    isVariation || index > 0,
                                    index > 0
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  };

                  /*
                   * La línea principal utiliza exactamente el mismo modelo
                   * jerárquico que las variantes.
                   *
                   * La diferencia es solamente semántica: el primer hijo
                   * (children[0]) es la continuación principal y los hijos
                   * posteriores son variantes hermanas.
                   *
                   * No hacemos ningún cálculo a partir del FEN.
                   */
                  const renderMainline = (startId) => {
                    return renderVariationBranch(
                      startId,
                      'analysis-mainline',
                      0
                    );
                  };

                  const rootChildren = (root?.childrenIds || [])
                    .map((childId) => moveTree.nodes[childId])
                    .filter(Boolean);

                  const firstChild = rootChildren[0] || null;
                  const rootVariations = rootChildren.slice(1);

                  return (
                    <div
                      data-testid="analysis-moves-panel"
                      className="flex-1 min-h-0 flex flex-col space-y-2"
                    >
                      <div className="text-[10px] font-semibold uppercase tracking-widest text-amber-600">
                        Análisis · árbol de jugadas
                      </div>

                      {firstChild ? (
                        <div
                          data-testid="analysis-tree"
                          className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden font-mono text-xs leading-relaxed"
                        >
                          <button
                            type="button"
                            data-testid="analysis-tree-root-btn"
                            onClick={() => analysisGoto(moveTree.rootId)}
                            className={`inline-flex items-center cursor-pointer px-1.5 py-0.5 rounded mb-1 ${
                              currentId === moveTree.rootId
                                ? 'bg-amber-300 font-bold text-slate-900'
                                : 'text-slate-400 hover:bg-slate-100'
                            }`}
                          >
                            Inicio
                          </button>

                          <div className="space-y-0.5">
                            {renderMainline(firstChild.id)}

                            {rootVariations.length > 0 && (
                              <div
                                data-testid="analysis-root-variations"
                                className="ml-2 pl-2 border-l-2 border-amber-200 space-y-0.5"
                              >
                                {rootVariations.map((variation) => (
                                  <div
                                    key={`analysis-root-variation-${variation.id}`}
                                    data-testid={`analysis-tree-variation-${variation.id}`}
                                    className="relative"
                                  >
                                    <span
                                      aria-hidden="true"
                                      className="absolute -left-2 top-3 w-2 border-t border-amber-200"
                                    />
                                    {renderVariationBranch(
                                      variation.id,
                                      `root-${variation.id}`,
                                      0,
                                      true,
                                      true
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div
                          data-testid="analysis-line-root"
                          className="text-sm text-slate-500"
                        >
                          Posición inicial.
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : !canSeeMoves ? (
                <div className="text-sm text-slate-500 text-center py-12">El profesor ha ocultado los movimientos.</div>
              ) : movesForDisplay.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-6">Aún no hay movimientos.</div>
              ) : (
                <div className="font-mono text-sm leading-relaxed">
                  {movesForDisplay.map((m, i) => (
                    <span key={i}
                      onClick={()=>{
                        const target = i + 1;
                        analysisGotoPly(target);
                      }}
                      className={`inline-block cursor-pointer px-1.5 py-0.5 rounded ${(ply ?? movesForDisplay.length) === i+1 ? 'bg-amber-200' : 'hover:bg-slate-100'}`}>
                      {i%2===0 && <span className="text-slate-400">{Math.floor(i/2)+1}.</span>} {m.san}
                    </span>
                  ))}
                </div>
              )}
              </div>
            </TabsContent>

            <TabsContent value="chat" className="flex-1 min-h-0 p-0 flex flex-col">
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {(room.chat || []).length === 0 && <div className="text-sm text-slate-500 text-center py-12">Sin mensajes.</div>}
                {(room.chat || []).map(m => {
                  const mine = m.user_id === user?.id;
                  return (
                    <div key={m.id} data-testid={`chat-msg-${m.id}`} className={mine ? 'text-right' : ''}>
                      {!mine && <div className="text-xs text-slate-500 font-semibold">{m.name}</div>}
                      <div className={`inline-block max-w-[85%] rounded-2xl px-3 py-1.5 text-sm ${mine ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-slate-100 rounded-bl-sm'}`}>
                        {m.text}
                      </div>
                    </div>
                  );
                })}
                <div ref={chatEndRef}/>
              </div>
              <form onSubmit={sendChat} className="p-3 border-t border-slate-200 flex gap-2">
                <Input data-testid="chat-input" placeholder="Escribe un mensaje…" value={chatText} onChange={(e)=>setChatText(e.target.value)}/>
                <Button data-testid="chat-send-btn" type="submit" size="icon" className="rounded-full bg-blue-600 hover:bg-blue-700"><Send className="w-4 h-4"/></Button>
              </form>
            </TabsContent>

            <TabsContent value="leaderboard" className="flex-1 min-h-0 p-4 overflow-y-auto">
              <LeaderboardPanel room={room} isTeacher={isTeacher} onReset={resetSession}/>
            </TabsContent>

            <TabsContent
              key={takingAttendance ? 'participants-attendance' : 'participants'}
              value="participants"
              className={takingAttendance
                ? "absolute inset-x-0 top-12 bottom-0 min-h-0 overflow-y-auto p-4 !mt-0"
                : "flex-1 min-h-0 p-0 !mt-0 overflow-hidden"}
            >
              {(() => {
                const students = (room.participants || []).filter(p => p.role === 'student');
                const presentStudents = students.filter(p => p.is_present).length;
                const attendancePresent = students.filter(p => p.attendance_status === 'present').length;
                const attendanceLate = students.filter(p => p.attendance_status === 'late').length;
                const attendanceAbsent = students.filter(p => p.attendance_status === 'absent').length;
                const attendanceUnmarked = students.length - attendancePresent - attendanceLate - attendanceAbsent;
                const today = new Date().toLocaleDateString('es-MX', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                });

                return (
                  <div
                    ref={attendancePanelRef}
                    className={takingAttendance ? "min-h-0" : "h-full min-h-0 overflow-y-auto p-4"}
                    style={{ overflowAnchor: 'none' }}
                  >
                    <div className="mb-4 rounded-lg border bg-slate-50 p-3">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div>
                          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">
                            Alumnos
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {presentStudents} de {students.length} dentro del salón
                          </div>
                        </div>

                        {isTeacher && (
                          <Button
                            data-testid="attendance-toggle"
                            size="sm"
                            variant={takingAttendance ? "default" : "outline"}
                            onClick={() => setTakingAttendance(prev => !prev)}
                          >
                            {takingAttendance ? 'Cerrar lista' : 'Pasar lista'}
                          </Button>
                        )}
                      </div>

                      {isTeacher && (
                        <>
                          <div className="text-xs text-slate-500 mb-3 capitalize">
                            Asistencia de hoy — {today}
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                            <div className="rounded-md bg-white border px-2 py-1.5">
                              <span className="font-semibold">✅ {attendancePresent}</span>
                              <span className="text-slate-500 ml-1">Presentes</span>
                            </div>
                            <div className="rounded-md bg-white border px-2 py-1.5">
                              <span className="font-semibold">🕐 {attendanceLate}</span>
                              <span className="text-slate-500 ml-1">Retardos</span>
                            </div>
                            <div className="rounded-md bg-white border px-2 py-1.5">
                              <span className="font-semibold">❌ {attendanceAbsent}</span>
                              <span className="text-slate-500 ml-1">Faltas</span>
                            </div>
                            <div className="rounded-md bg-white border px-2 py-1.5">
                              <span className="font-semibold">⬜ {attendanceUnmarked}</span>
                              <span className="text-slate-500 ml-1">Sin marcar</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="divide-y">
                      {(room.participants || []).map(p => {
                        const muted = (room.muted_user_ids || []).includes(p.user_id);
                        const handUp = (room.raised_hands || []).find(h => h.user_id === p.user_id);
                        const attendanceStatus = p.attendance_status || '';

                        return (
                          <div
                            key={p.user_id}
                            data-testid={`participant-${p.user_id}`}
                            className={`py-3 flex items-center justify-between gap-3 text-sm ${handUp ? 'bg-amber-50 rounded-lg px-2' : ''}`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className={`w-8 h-8 rounded-full grid place-items-center font-bold text-white shrink-0 ${p.role === 'teacher' ? 'bg-blue-600' : 'bg-amber-500'}`}>
                                {p.name?.[0]}
                              </div>

                              <div className="min-w-0">
                                <div className="font-semibold flex items-center gap-1">
                                  <span className="truncate">{p.name}</span>
                                  {handUp && <Hand data-testid={`hand-${p.user_id}`} className="w-4 h-4 text-amber-500 animate-pulse shrink-0"/>}
                                  {muted && <VolumeX className="w-3.5 h-3.5 text-rose-500 shrink-0"/>}
                                </div>

                                <div className="text-xs text-slate-500 capitalize flex items-center gap-1.5 flex-wrap">
                                  {p.role?.replace('_',' ')}

                                  {p.role === 'student' && (
                                    <span className={p.is_present ? 'text-emerald-600 font-semibold' : 'text-slate-400 font-semibold'}>
                                      {p.is_present ? '· En el salón' : '· Fuera del salón'}
                                    </span>
                                  )}

                                  {handUp && (
                                    <span className="ml-1 text-amber-700 font-semibold">
                                      · mano levantada {new Date(handUp.ts).toLocaleTimeString()}
                                    </span>
                                  )}
                                </div>

                                {p.role === 'student' && (
                                  <div className="mt-1.5 flex items-center gap-2">
                                    <span className="text-xs text-slate-500">Asistencia:</span>

                                    {isTeacher && takingAttendance ? (
                                      <select
                                        data-testid={`attendance-${p.user_id}`}
                                        value={attendanceStatus}
                                        onChange={(e) => setAttendance(
                                          p.user_id,
                                          e.target.value === '' ? null : e.target.value
                                        )}
                                        className="h-7 rounded-md border border-slate-300 bg-white px-2 text-xs font-medium text-slate-700 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                      >
                                        <option value="">⬜ Sin marcar</option>
                                        <option value="present">✅ Presente</option>
                                        <option value="late">🕐 Retardo</option>
                                        <option value="absent">❌ Falta</option>
                                      </select>
                                    ) : (
                                      <span className="text-xs font-semibold text-slate-600">
                                        {attendanceStatus === 'present' && '✅ Presente'}
                                        {attendanceStatus === 'late' && '🕐 Retardo'}
                                        {attendanceStatus === 'absent' && '❌ Falta'}
                                        {!attendanceStatus && '⬜ Sin marcar'}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {isTeacher && p.role !== 'teacher' ? (
                              <div className="flex items-center gap-1 shrink-0">
                                {handUp && (
                                  <Button
                                    data-testid={`dismiss-hand-${p.user_id}`}
                                    size="sm"
                                    variant="ghost"
                                    className="text-amber-700 hover:bg-amber-100"
                                    onClick={()=>dismissHand(p.user_id)}
                                  >
                                    Atender
                                  </Button>
                                )}

                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button data-testid={`participant-menu-${p.user_id}`} size="icon" variant="ghost">
                                      <MoreVertical className="w-4 h-4"/>
                                    </Button>
                                  </DropdownMenuTrigger>

                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem data-testid={`mute-${p.user_id}`} onClick={()=>toggleMute(p.user_id, !muted)}>
                                      {muted ? <><Volume2 className="w-4 h-4 mr-2"/>Quitar silencio</> : <><VolumeX className="w-4 h-4 mr-2"/>Silenciar</>}
                                    </DropdownMenuItem>

                                    <DropdownMenuItem data-testid={`kick-${p.user_id}`} onClick={()=>kickParticipant(p.user_id)} className="text-rose-600">
                                      <UserX className="w-4 h-4 mr-2"/>Expulsar
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            ) : (
                              <div className="text-xs text-slate-400 font-mono shrink-0">
                                {new Date(p.joined_at).toLocaleTimeString()}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </TabsContent>
          </Tabs>
        </aside>
        )}

        </div>
      </div>

      {/* ─── Chat flotante del PROFESOR (Opción A) — reutiliza room.chat/sendChat ───
          Capa de presentación sobre Classroom. NO toca el panel lateral ni el
          estado de la clase. Desktop/tablet: mini panel anclado abajo-derecha.
          Móvil: hoja inferior a ancho completo. z-[65] (bajo ChallengeIntro z-[70]). */}
      {isTeacher && chatPopoverOpen && (
        <>
          {isMobile && (
            <div data-testid="floating-chat-backdrop" onClick={()=>setChatPopoverOpen(false)}
              className="fixed inset-0 z-[64] bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200" />
          )}
          <div data-testid="floating-chat-panel"
            className={`fixed z-[65] bg-white shadow-2xl border border-slate-200 flex flex-col overflow-hidden ${isMobile
              ? 'inset-x-0 bottom-0 h-[70vh] max-h-[480px] rounded-t-2xl border-t animate-in slide-in-from-bottom duration-300'
              : 'bottom-5 right-5 w-[340px] max-w-[calc(100vw-2.5rem)] h-[70vh] max-h-[480px] rounded-2xl animate-in slide-in-from-bottom-2 fade-in duration-200'}`}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0">
              <div className="font-display font-extrabold text-sm uppercase tracking-widest text-slate-600 flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-blue-500"/> Chat
              </div>
              <Button data-testid="floating-chat-close-btn" size="icon" variant="ghost" className="rounded-full h-7 w-7" onClick={()=>setChatPopoverOpen(false)}>
                <X className="w-4 h-4"/>
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {(room.chat || []).length === 0 && <div className="text-sm text-slate-500 text-center py-12">Sin mensajes.</div>}
              {(room.chat || []).map(m => {
                const mine = m.user_id === user?.id;
                return (
                  <div key={m.id} data-testid={`floating-chat-msg-${m.id}`} className={mine ? 'text-right' : ''}>
                    {!mine && <div className="text-xs text-slate-500 font-semibold">{m.name}</div>}
                    <div className={`inline-block max-w-[85%] rounded-2xl px-3 py-1.5 text-sm ${mine ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-slate-100 rounded-bl-sm'}`}>
                      {m.text}
                    </div>
                  </div>
                );
              })}
              <div ref={chatEndRef}/>
            </div>
            <form onSubmit={sendChat} className="p-3 border-t border-slate-200 flex gap-2">
              <Input data-testid="floating-chat-input" placeholder="Escribe un mensaje…" value={chatText} onChange={(e)=>setChatText(e.target.value)}/>
              <Button data-testid="floating-chat-send-btn" type="submit" size="icon" className="rounded-full bg-blue-600 hover:bg-blue-700"><Send className="w-4 h-4"/></Button>
            </form>
          </div>
        </>
      )}

      <Dialog open={!!launchCfg} onOpenChange={(v)=>{ if(!v) setLaunchCfg(null); }}>
        <DialogContent data-testid="launch-config-dialog">
          <DialogHeader>
            <DialogTitle>Lanzar ejercicio</DialogTitle>
            <DialogDescription>Configura los puntos y el tiempo antes de enviarlo a los alumnos.</DialogDescription>
          </DialogHeader>
          {launchCfg && (
            <div className="space-y-3">
              <div className="text-sm font-semibold">{launchCfg.title}</div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500">Puntos</label>
                  <Input data-testid="launch-points" type="number" value={launchCfg.points}
                    onChange={(e)=>setLaunchCfg(c=>({...c, points:e.target.value}))}/>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500">Duración (seg)</label>
                  <Input data-testid="launch-duration" type="number" value={launchCfg.duration_seconds}
                    onChange={(e)=>setLaunchCfg(c=>({...c, duration_seconds:e.target.value}))}/>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500">Intentos</label>
                  <Input data-testid="launch-attempts" type="number" value={launchCfg.max_attempts}
                    onChange={(e)=>setLaunchCfg(c=>({...c, max_attempts:e.target.value}))}/>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch data-testid="launch-speed-bonus" checked={!!launchCfg.speed_bonus}
                  onCheckedChange={(v)=>setLaunchCfg(c=>({...c, speed_bonus:v}))}/>
                <span className="text-sm text-slate-600">Bonus por rapidez (más puntos cuanto más rápido resuelvan)</span>
              </div>
              {(launchCfg.solution_moves || []).length === 0 && !launchCfg.capture_config && launchCfg.type !== 'play_computer' && (
                <div className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5"/> Este capítulo no tiene jugadas solución; los alumnos no podrán resolverlo.</div>
              )}
              {launchCfg.capture_config && (
                <div className="text-xs text-indigo-600 flex items-center gap-1"><Send className="w-3.5 h-3.5"/> Capture Activity — se enviará con su pieza, objetos y obstáculos.</div>
              )}
              {launchCfg.type === 'play_computer' && (
                <div data-testid="launch-pc-hint" className="text-xs text-indigo-600 flex items-center gap-1"><Send className="w-3.5 h-3.5"/> Play with Computer — objetivo: <b>{launchCfg.goal === 'draw' ? 'Empatar' : 'Ganar'}</b> · alumno: <b>{launchCfg.student_side === 'black' ? 'Negras' : 'Blancas'}</b> · motor nivel <b>{launchCfg.engine_level}</b></div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button data-testid="launch-confirm-btn" onClick={doLaunch} className="bg-amber-500 hover:bg-amber-600">
              <Send className="w-4 h-4 mr-1"/> Lanzar a la clase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* C1 · Explanation dirigida — vista del alumno (overlay). Reutiliza el
          ExplanationViewer real vía wrapper, sin modificarlo. */}
      {studentExplanationMode && (
        room.active_explanation?.activity?.type === 'lava_floor' ? (
          <ClassroomLavaPresentation
            activeExplanation={room.active_explanation}
            roomId={id}
            isTeacher={false}
          />
        ) : (
          <>
            <ClassroomExplanationView
              activeExplanation={room.active_explanation}
              roomId={id}
              canRespond
              challengeIntroVisible={introVisible}
            />
            {/* FASE A · Challenge Intro (SOLO alumno). Overlay presentacional;
                la detección/guard/timer viven en el efecto de arriba. */}
            <ChallengeIntro
              visible={introVisible}
              challengeType={introType}
              onDismiss={() => { if (introTimerRef.current) clearTimeout(introTimerRef.current); setIntroVisible(false); }}
            />
          </>
        )
      )}

      {/* C2 · Explanation dirigida — vista + controles del PROFESOR. El
          profesor ve el mismo Step que los alumnos (overlay read-only) y una
          barra fija para navegar el Step oficial y finalizar. z-[60] para
          quedar por encima del Dialog del Viewer (z-50). */}
      {isTeacher && room.active_explanation && (
        <>
          {room.active_explanation?.activity?.type === 'lava_floor' ? (
            <ClassroomLavaPresentation
              activeExplanation={room.active_explanation}
              roomId={id}
              isTeacher
            />
          ) : (
            <>
              <ClassroomExplanationView
                activeExplanation={room.active_explanation}
                roomId={id}
                canRespond={false}
                isTeacher
                teacherPanelRoom={room}
                teacherNavigation={{
                  currentStep: Number(room.active_explanation.current_step) || 0,
                  totalSteps: Number(room.active_explanation.total_steps) || 1,
                  onPrevious: () => setExplanationStep((Number(room.active_explanation.current_step) || 0) - 1),
                  onNext: () => setExplanationStep((Number(room.active_explanation.current_step) || 0) + 1),
                  onFinish: stopExplanation,
                }}
              />
            </>
          )}
          {(() => {
            const active = room.active_explanation;
            const cur = Number(active.current_step) || 0;
            const total = Number(active.total_steps) || 1;
            const isLava = active?.activity?.type === 'lava_floor';
            const exerciseIndex = Number(active.current_activity_index) || 0;
            const exerciseTotal = Number(active.total_lava_activities) || 1;
            const previous = () => isLava
              ? setLavaExercise(exerciseIndex - 1)
              : setExplanationStep(cur - 1);
            const next = () => isLava
              ? setLavaExercise(exerciseIndex + 1)
              : setExplanationStep(cur + 1);
            if (!isLava) return null;
            return (
              <div data-testid="teacher-explanation-controls"
                className="fixed bottom-1 left-1/2 -translate-x-1/2 z-[60] pointer-events-auto flex items-center gap-2 rounded-2xl bg-white shadow-2xl border border-slate-200 px-3 py-2">
                <Button data-testid="tex-prev" size="sm" variant="outline" className="rounded-xl"
                  onClick={previous}
                  disabled={isLava ? exerciseIndex <= 0 : cur <= 0}>
                  <ChevronLeft className="w-4 h-4 mr-1"/> Anterior
                </Button>
                <span data-testid="tex-progress" className="text-sm font-bold tabular-nums px-2 whitespace-nowrap">
                  {isLava ? 'Ejercicio' : 'Paso'} {isLava ? exerciseIndex + 1 : cur + 1} de {isLava ? exerciseTotal : total}
                </span>
                <Button data-testid="tex-next" size="sm" className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
                  onClick={next}
                  disabled={isLava ? exerciseIndex >= exerciseTotal - 1 : cur >= total - 1}>
                  Siguiente <ChevronRight className="w-4 h-4 ml-1"/>
                </Button>
                <Button data-testid="tex-finish" size="sm" variant="destructive" className="rounded-xl"
                  onClick={stopExplanation}>
                  <Flag className="w-4 h-4 mr-1"/> Finalizar
                </Button>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
